/**
 * Interface copy, Chinese. The one table every other language is typed
 * against: add a key here and en.ts / de.ts fail to compile until they carry
 * it too (BLUEPRINT §1: one table; the extension gets its labels from the
 * same place through /api/ext/seen).
 *
 * Only the interface. The agent's own prose — a judgment, a strategy, a
 * company blurb — is written once, in English, and rendered into the
 * reader's language through the render cache (lib/domain/render.ts, #255).
 * `%1` `%2` are positional slots for fmt() in index.ts.
 */
export const zh = {
  // shell
  "nav.label": "主导航",
  "nav.overview": "总览",
  "nav.jobs": "岗位",
  "nav.companies": "公司",
  "nav.interviews": "面试",
  "nav.me": "我的资料",
  "nav.agent": "Agent",
  "nav.collapse": "收起",
  "nav.expand": "展开",
  "nav.loading": "加载中...",
  "nav.cardFailed": "没能打开卡片，请再试一次。",
  "nav.areaFailed": "没加载出来，请再试一次。",
  "nav.offline": "网络已断开，重新连接后再试。",
  "nav.slow": "还在加载，比平时慢一些...",
  "nav.failed": "页面出错了，请重试。",
  "nav.dismiss": "关闭",
  "theme.toDark": "切换到夜间",
  "theme.toLight": "切换到日间",
  "lang.label": "语言",
  "help.label": "帮助与联系",
  "help.lede": "遇到问题或有建议，写信给我们：",
  "help.copy": "复制",
  "help.copied": "已复制",
  "help.copyByHand": "没能自动复制，地址已经选中，请手动复制。",
  "auth.support": "遇到问题？写信给",
  "fold.open": "展开 →",

  // agent panel (the container; 4e wires the thread)
  "chat.new": "新对话",
  "chat.expand": "展开对话",
  "chat.min": "收起",
  "chat.dock": "吸附到右侧",
  "chat.undock": "取消右侧吸附",
  "chat.dockNarrow": "窄屏不能吸附到右侧",
  "chat.resizeW": "对话面板宽度",
  "chat.resizeH": "对话面板高度",
  "chat.end": "结束会话并记下要点",
  "chat.endMock": "结束面试并听总评",
  "chat.endAsk": "结束这段会话？我会把聊到的要点记进你的画像，要十几秒；之后再聊是新的一段。",
  "chat.endAskMock": "结束这场模拟面试？面试官会写一段总评，要十几秒。",
  "chat.cancel": "取消",
  "chat.endOk": "结束",
  "chat.title": "新对话",
  "chat.sub": "随时可以问",
  "chat.opener": "有什么可以帮你的？想从哪一件开始？",
  "chat.placeholder": "问一句…",
  "chat.send": "发送",
  "chat.attach": "加文件",
  "chat.attachKinds": "加文件：PDF、Word、Excel、PowerPoint、.txt 或 .md，或图片",
  "chat.attachFailed": "这个文件没能传上去。",
  "chat.hereIsFile": "看看这个。",
  "chat.afterActions": "上面这些我记下了。回我一句，我接着问。",
  "chat.cutCeiling": "这条回答太长，没写完。回「继续」我接着说。",
  "chat.cutStopped": "这条回答中途断了，换个说法再问一次吧。",
  "chat.drop": "松手就把文件加进来",
  "chat.remove": "去掉",
  "chat.cvSaved": "已存成你的简历：%1",
  "chat.tailorProposed": "提了 %1 处改动（其中扩充 %2 处）",
  "chat.tailorDecided": "接受 %1 处（其中扩充 %2 处），拒绝 %3 处",
  "chat.cvReplaced": "已存成你的简历：%1，替换了原来的 %2",
  "chat.cvReread": "重新读一遍你的简历：%1",
  "chat.tooMany": "一次最多 %1 个文件",
  "modal.close": "关掉",
  "modal.cancel": "取消",
  "modal.gone": "这一条已经不在了。",
  "thread.mock": "模拟 · %1",
  "thread.tailor": "改简历 · %1",
  "thread.interview": "面试 · %1",
  "thread.profile": "画像",
  "thread.criteria": "规则",
  "thread.draft": "帮写 · %1 · %2",
  "thread.draftLoose": "帮写 · %1",
  "thread.free": "新对话",
  "thread.untitled.opening": "一条岗位",
  "thread.untitled.company": "一家公司",
  "thread.untitled.interview": "一场面试",
  "thread.untitled.mock": "一次模拟面试",
  "thread.untitled.tailor": "一次简历改写",
  "thread.channel.whatsapp": "WhatsApp",
  "thread.channel.slack": "Slack",
  "thread.channel.telegram": "Telegram",
  "chan.endWord": "结束会话",
  "chan.endHint": "（发送「结束会话」即可结束当前会话。）",
  "chan.ended": "会话已结束。",
  "chan.endedIdle": "当前会话因没有更多回应而结束。",
  "chan.nothingToEnd": "没有进行中的会话。",
  "chan.wrote": "记下了：%1",
  "chan.learned": "有 %1 条规则等你在网页上确认：%2",
  "chan.unknown": "这个聊天还没有连接到账号。到「我的资料 → 设置」里连接：%1",
  "chan.many": "这个聊天连着不止一个账号，先在设置里只留一个。",
  "chan.off": "这里还没开聊天。到设置页打开：%1",
  "chan.trialOver": "免费的一周已经用完，到网页上看看：%1",
  "chan.failed": "这一句没能回上，稍后再发一次。",
  "chan.badFile": "这个文件没能收下：%1",
  "chan.onlyThese": "只看文字、PDF 和图片。",
  "me.set.ch.chat": "聊天",
  "me.set.ch.chatHint": "开着时，从这里发来的消息由 agent 回答；发「结束会话」结束一段会话。",
  "chan.slack.noFiles": "文件请在网页或 WhatsApp 里发；这里只看文字。",
  "me.set.ch.wa.lede": "连接后可以在 WhatsApp 里直接和 agent 聊（推送还没接上）；连接是在手机上把一个码发过来。",
  "me.set.ch.wa.connect": "连接 WhatsApp",
  "me.set.ch.wa.unavailable": "这个部署还没配 WhatsApp 号码，暂时连不了。",
  "me.set.ch.tg.lede": "推送发到 Telegram，逐条带按钮：早报里的岗位当场答「去申请 / 先收着 / 不适合我」，问题直接回复那条消息就能答；也能在 Telegram 里直接和 agent 聊。连接是在 Telegram 里按一次 Start。",
  "me.set.ch.tg.connect": "连接 Telegram",
  "me.set.ch.tg.open": "在 Telegram 里按 Start",
  "me.set.ch.tg.valid": "还有 %1 分钟有效；按完 Start 后刷新本页。",
  "me.set.ch.tg.unavailable": "这个部署还没配 Telegram bot，暂时连不了。",
  "me.set.ch.tg.connected": "已连接的 Telegram",
  "me.set.ch.wa.open": "在 WhatsApp 里发送连接码",
  "me.set.ch.wa.code": "或把这个码发给上面的号码：%1",
  "me.set.ch.wa.valid": "还有 %1 分钟有效；发出后刷新本页。",
  "chan.wa.linked": "连上了：这个号现在连着 %1 的账号，能和 agent 聊；发「结束会话」结束一段会话。",
  "chan.wa.expired": "这个连接码已经过期或用过了。回「我的资料 → 设置」再点一次「连接 WhatsApp」。",
  "chan.wa.conflict": "这个手机号已经属于另一个 Counting AI 账号，不能转移。请登录原账号，或用原账号邮箱找回密码。",
  "chan.tg.linked": "连上了：这个 Telegram 现在连着 %1 的账号，早报和问题会推到这里，也能直接和 agent 聊；发「结束会话」结束一段会话。推哪些在「我的资料 → 设置」里选。",
  "chan.tg.expired": "这个连接码已经过期或用过了。回「我的资料 → 设置」再点一次「连接 Telegram」。",
  "chan.tg.conflict": "这个 Telegram 已经连着另一个 Counting AI 账号，不能转移。先在那个账号的设置里移除它，再来连接。",
  "chan.tg.hello": "这里是 Counting AI 的推送。要连接，到「我的资料 → 设置」点「连接 Telegram」：%1",

  // the Agent page and the panel's thread (block 4e)
  "kind.all": "全部",
  "kind.opening": "岗位",
  "kind.company": "公司",
  "kind.interview": "面试",
  "kind.mock": "模拟",
  "kind.tailor": "改简历",
  "kind.meta": "画像 · 规则",
  "kind.profile": "画像",
  "kind.criteria": "规则",
  "kind.free": "对话",
  "agent.search": "搜对话…",
  "agent.newThread": "＋ 新对话",
  "agent.today": "今天",
  "agent.week": "本周",
  "agent.earlier": "更早",
  "agent.none": "没有匹配的对话",
  "agent.pick": "选一条对话，或开一条新的。",
  "agent.unread": "有新回复",
  "agent.open.opening": "打开这条岗位 →",
  "agent.open.company": "打开公司卡 →",
  "agent.open.interview": "打开面试准备 →",
  "agent.open.profile": "打开画像 →",
  "agent.open.criteria": "打开规则 →",
  "agent.mockPlaceholder": "用英文回答…（说「用中文」整场切换，「结束」给总评）",
  "agent.folded": "之前 %1 轮已折成摘要 · 展开",
  "agent.ended": "上次会话结束于 %1",
  "agent.endedNothing": "这次没有要记的。",
  "agent.more": "看更早的 %1 条",
  "agent.loading": "在读…",
  "agent.thinking": "在想…",
  "agent.looking": "正在看：",
  "agent.failed": "没接上。再试一次？",
  "agent.endFailed": "没结束成，再点一次。",
  "agent.ending": "正在结束会话，把要点记进你的画像…",
  "agent.endingMock": "面试官在写总评…",
  "agent.capped": "只显示最近 100 条，搜索能找到更早的。",
  "opener.opening": "%1 · %2%3",
  "opener.askThis": "问这条",
  "opener.notNow": "先不问",
  "opener.company": "%1 · 匹配在招 %2 条 · 签证：%3",
  "opener.askCompany": "这家值得跟吗",
  "opener.interview": "准备材料 %1/4 已齐。",
  "opener.missing": "缺：%1。",
  "opener.askPress": "先过一遍会被质疑的点",
  "opener.mock": "模拟面试，准备材料 %1/4。说「开始」我就出第一题。",
  "opener.tailor": "为「%1」改简历。说「开始」，我先看岗位要求和两位模拟筛选方的判断，再一条条提改动。",
  "opener.tailorKept": "「%1」的这份改写是在你之前那份简历上做的，保持当时的样子：可以看最终版本、下载。要改，请在页面上为这个岗位重新开始改写。",
  "opener.start": "开始",
  "opener.profile": "这里说的关于你的事会写进画像。",
  "opener.criteria": "这里改的规则下次运行生效。",
  "opener.rules": "有几条规则等你确认。",
  "opener.pending": "还有几件事需要你处理。",
  "prep.deep": "深挖",
  "action.confirm.learned.yes": "记下这条规则",
  "action.confirm.learned.no": "不要",
  "action.confirm.before": "之前已经答过，没有改动",

  // statuses (domain/status.ts)
  "status.maybe": "收藏",
  "status.passed": "已排除",
  "status.preparing": "待投",
  "status.applied": "已投",
  "status.interview_requested": "面试邀请",
  "status.interviewing": "面试中",
  "status.offer": "offer",
  "status.rejected": "已拒",

  // match band
  "level.high": "高匹配",
  "level.mid": "中匹配",
  "level.low": "低匹配",
  "level.unrated": "未评估",

  // the four flags and what they can say
  "flag.visa": "签证",
  "flag.lang": "语言",
  "flag.move": "地点",
  "flag.salary": "薪资",
  "basis.stated": "明说",
  "basis.inferred": "推断",
  "basis.unknown": "未说明",
  "card.flagNone": "岗位描述里没提到。",
  "visa.stated_yes": "会办",
  "visa.likely": "推测会办",
  "visa.unknown": "未知",
  "visa.stated_no": "不办",
  "local.none": "语言你都会",
  "local.plus": "本地语言加分",
  "local.required": "要求你不会的语言",
  "local.unknown": "未说明",
  "move.no_move": "不用搬",
  "move.remote_home": "本国远程",
  "move.in_country": "本国",
  "move.move_home": "本国要搬",
  "move.remote_abroad": "境外远程",
  "move.move_abroad": "要出国",
  "move.unknown": "未说明",
  "pay.clears_threshold": "过门槛",
  "pay.below_threshold": "低于门槛",
  "pay.not_stated": "未公开",
  "mode.remote": "远程",
  "mode.hybrid": "混合",
  "mode.onsite": "到岗",
  "mode.unclear": "未表明",

  // opening card
  "card.original": "原始页面 ↗",
  "card.undo": "撤销",
  "card.why": "为什么是你",
  "card.concerns": "我的顾虑",
  "card.goApply": "去申请",
  "card.goApplyArrow": "去申请 →",
  "card.save": "先收着",
  "card.notFor": "不适合我",
  "card.tailor": "为这个岗位改简历",
  "card.tailorOthers": "其他改写",
  "card.tailorEdited": "更新于 %1",
  "card.tailorStale": "基于之前的简历",
  "card.tailorView": "查看改过的简历",
  "card.tailoredFold": "改过的简历",
  "tailor.title": "改简历",
  "tailor.back": "← 回到岗位",
  "tailor.return": "← 返回",
  "tailor.backAgent": "← Agent",
  "tailor.strength.nudge": "力度：只改措辞",
  "tailor.strength.keywords": "力度：对齐招聘用词",
  "tailor.strength.full": "力度：充分改写",
  "tailor.all": "全部接受（%1 条）",
  "tailor.allExpansions": "全部接受（%1 条，含 %2 条扩充）",
  "tailor.none": "还没有改动。在对话里说「开始」：先读岗位要求，再一条条提改动，每条都由你接受或拒绝。",
  "tailor.accept": "接受",
  "tailor.reject": "拒绝",
  "tailor.withdraw": "撤回",
  "tailor.reaccept": "改为接受",
  "tailor.working.accept": "正在接受…",
  "tailor.working.reject": "正在拒绝…",
  "tailor.working.withdraw": "正在撤回…",
  "tailor.working.reaccept": "正在改为接受…",
  "tailor.working.all": "正在全部接受…",
  "tailor.state.proposed": "待定",
  "tailor.state.accepted": "已接受",
  "tailor.state.rejected": "已拒绝",
  "tailor.expansion": "扩充",
  "tailor.expansionWhy": "简历里没有写到这一点，接受前请确认属实。",
  "tailor.src.resume": "依据：简历 %1",
  "tailor.src.said": "依据：%1 在对话里说「%2」",
  "tailor.src.none": "依据：简历里没有",
  "tailor.serves": "对应要求 %1",
  "tailor.flag.term": "新名字：%1",
  "tailor.flag.number": "新数字：%1",
  "tailor.flag.year": "新年份：%1",
  "tailor.flag.authorship": "别人做的写成了自己做的：%1",
  "tailor.flag.cited": "引用了另一段经历的内容",
  "tailor.flag.said": "对话里找不到这句原话",
  "tailor.flag.skills": "删掉了技能",
  "tailor.flag.long": "比原句长很多",
  "tailor.act.replace": "修改",
  "tailor.act.add": "新增",
  "tailor.act.remove": "删除",
  "tailor.act.move": "移到 %1 之后",
  "tailor.fact": "事实行（雇主、头衔、日期）不改",
  "tailor.section.summary": "简介",
  "tailor.section.experience": "经历",
  "tailor.section.education": "教育",
  "tailor.section.skills": "技能",
  "tailor.section.publications": "出版物",
  "tailor.section.other": "其他",
  "tailor.view.merged": "合并显示",
  "tailor.view.split": "并排显示",
  "tailor.was": "原文",
  "tailor.now": "改后",
  "tailor.reqs": "招聘要求逐条对照",
  "tailor.reqsNote": "两位模拟筛选方各自逐条判断简历里有没有证据，不打总分。",
  "tailor.reqsNone": "岗位要求还没读。在对话里说「开始」就会读。",
  "tailor.before": "改前",
  "tailor.after": "改后",
  "tailor.screener": "筛选方 %1",
  "tailor.recheck": "再检查",
  "tailor.checking": "正在重新检查…",
  "tailor.checkFailed": "重新检查失败了，请再试一次。",
  "tailor.notYet": "未检查",
  "tailor.must": "必须",
  "tailor.nice": "加分",
  "tailor.verdict.evidence": "有证据",
  "tailor.verdict.weak": "只提到",
  "tailor.verdict.none": "没有",
  "tailor.verdict.unknown": "未答",
  "tailor.stale": "这份改写是在你之前那份简历上做的，下面是它当时的最终版本，可以下载。要在现在的简历上修改，请重新开始。",
  "tailor.noOutline": "简历还要再读一次，才能在这里逐行显示。",
  "tailor.pdfNote": "原件是 PDF：导出时会用单栏版式重新排版，版式和原件不同；Word 原件则是在原文件上直接改。",
  "tailor.posting": "岗位原文",
  "tailor.sr.gone": "删去：",
  "tailor.sr.new": "加入：",
  "tailor.aria.accept": "接受 %1 的改动",
  "tailor.aria.reject": "拒绝 %1 的改动",
  "tailor.aria.withdraw": "撤回 %1 的改动",
  "tailor.aria.reaccept": "改为接受 %1 的改动",
  "tailor.restart": "为这个岗位重新开始改写",
  "tailor.afterOld": "「改后」一栏是在之后的接受或拒绝之前检查的，可能已经过时，请再检查。",
  "tailor.export": "导出 Word（改后）",
  "tailor.exportFirst": "导出前先再检查",
  "tailor.exportNone": "接受几条改动之后就能导出。",
  "tailor.exportPdfNew": "导出 PDF（改后）",
  "tailor.exportingWord": "正在准备 Word 文件…",
  "tailor.exportingPdf": "正在排版 PDF…",
  "tailor.exportNewNote": "原件是 PDF，改不进去：这两份是用你自己的文字重新排版的，内容是你的，版式和原件不同。Word 那份还能继续改。",
  "tailor.refusedNotOffered": "这种格式这份简历不提供。",
  "tailor.missedOne": "有 1 条接受的改动写不进 Word 原件（这行在表格或制表符分栏里），文件里没有它：",
  "tailor.missedMany": "有 %1 条接受的改动写不进 Word 原件（这些行在表格或制表符分栏里），文件里没有它们：",
  "tailor.refusedUnread": "简历还没有被读成一行一行的，先让它读一遍。",
  "tailor.refusedNoFile": "账号里现在没有简历文件，先上传一份。",
  "tailor.refusedBroken": "这份 Word 原件读不成当初那份简历了（可能之后在 Word 里改过）。重新上传一次简历，或者重新开始改写。",
  "tailor.exportFailed": "没导出来——是我们这边出了错，不是你的简历的问题。稍后再试一次。",
  "tailor.refusedBlank": "PDF 用的字体印不出这几行里的字，整行会变成空白：%1。用 Word 那份（它用读者自己的字体），或者把这些行改成这套字体有的字。",
  "tailor.refusedUnchecked": "这一版还没检查过（上次检查之后改过决定）：先再检查一次，再导出。",
  "tailor.damagedOne": "这份 Word 文件里有 1 个部件打不开（多半是在传输或保存时损坏的，不是这次改写造成的）。导出的文件里它原样保留着，简历的文字不受影响；Word 打开时它可能显示不出来：",
  "tailor.damagedMany": "这份 Word 文件里有 %1 个部件打不开（多半是在传输或保存时损坏的，不是这次改写造成的）。导出的文件里它们原样保留着，简历的文字不受影响；Word 打开时它们可能显示不出来：",
  "tailor.lostGlyphs": "PDF 里这些字符印不出来（用的字体没有它们）：%1。受影响的行：%2。Word 那份有它们——它用读者自己的字体。",
  "tailor.refusedUnreadable": "这份简历没能读出文字（之前读取被拒了）。换一份文件重新上传，再来导出。",
  "tailor.refusedMismatch": "简历的文字和读到的行对不上了（多半是刚换过简历，还没读完）。等它读完，或者重新开始一次改写。",
  "tailor.exportNote": "导出的是你的原文件，只有接受的那几行换了字；版式、字体、页眉都不动。",
  "tailor.smellsTitle": "导出前看一眼",
  "tailor.smellAi": "%1：「%2」读着像模型写的",
  "tailor.smellCoach": "%1：「%2」没说清你做了什么",
  "card.passPlaceholder": "一句话理由，我用来学",
  "card.passSubmit": "提交",
  "card.plugin": "插件会在申请页上帮你填能填的；填不了的它会标出来。",
  "card.closesOn": "%1 截止。",
  "card.changedHead": "招聘方改过这个岗位",
  "card.changedTitle": "标题现在是「%1」",
  "card.changedPlace": "地点现在是 %1",
  "card.changedText": "正文有较大改动",
  "card.changedJoin": "；",
  "card.changedEnd": "。以招聘方页面为准。",
  "card.closedTitle": "这个岗位已下架",
  "card.closedApply": "我们%1发现招聘方已撤下这个岗位，申请入口可能已失效。",
  "card.closedSent": "我们%1发现招聘方已撤下这个岗位，对方可能已不再处理新的申请。",
  "card.closedRunning": "我们%1发现招聘方已撤下招聘页；流程是否继续，以招聘方的消息为准。",
  "card.markApplied": "标已投",
  "card.notes": "批注",
  "card.notesPlaceholder": "给你自己看的",
  "card.unsave": "取消收藏",
  "card.dequeue": "移出待投",
  "card.sentDays": "投出 %1 天 · %2",
  "card.noReply": "还没回音。",
  "card.replyAsk": "我读到一封来自 %1 的邮件，像是回复。他们说的是？",
  "card.yes": "是",
  "card.no": "不是",
  "card.gotInvite": "收到面试邀请 →",
  "card.rejected": "被拒了",
  "card.otherTime": "换时间",
  "card.strategy": "策略",
  "card.skills": "技能",
  "card.accounts": "面经",
  "card.have": "已有",
  "card.adjacent": "沾边",
  "card.gap": "缺口",
  "card.mock": "模拟面试 →",
  "card.interviewStarted": "开始面试了",
  "card.gotOffer": "拿到 offer",
  "card.timeline": "这一场的时间线",
  "card.replyBy": "%1 前答复。",
  "card.openCompare": "打开对比 →",
  "ask.compare": "帮我对比手上的 offer。",
  "card.prepFold": "面试准备",
  "card.scoreHow": "这个分数是十项判断按你的权重合出来的，满分 100。",
  "card.scoreWeights": "看我怎么排序 →",

  // ⓘ beside a number: the button's name for a screen reader (#238)
  "tip.score": "这个分数是什么意思",
  "tip.closing": "这个数字数的是什么",
  "tip.scan": "这几个数字是什么",
  "tip.ledger": "这些数字怎么算",
  "tip.unrated": "「未评估」是什么意思",
  "tip.gone": "「已下架」是什么意思",
  "tip.track": "这些计数怎么算",
  "tip.report": "这些版本号是什么意思",
  "tip.week": "这两列怎么算",
  "tip.accounts": "面经来源和检索说明",
  "tip.deepSources": "深度调研的资料来源",
  "card.endedNote": "不会再出现在前排。",
  "card.judgmentFold": "判断依据",
  "card.companyLink": "公司 · 签证记录 →",
  "card.posting": "岗位原文",
  "card.postingMore": "展开全文",
  "card.postingLess": "收起",
  "card.translated": "译文",
  "card.translating": "翻译中…",
  "card.tooLongToTranslate": "太长，未翻译",
  "card.untranslatable": "翻译不出来",
  "card.threadFold": "这条岗位的线程",
  "card.continue": "接着说…",
  "card.seenOn": "见于 %1 个板子：%2",
  "reason.stack": "栈不沾边",
  "reason.too_senior": "级别偏高",
  "reason.too_junior": "级别偏低",
  "reason.location": "地点",
  "reason.pay": "薪资",
  "reason.size": "公司规模",
  "reason.unsure": "说不清",

  // company card
  "co.following": "在跟",
  "co.declined": "不跟",
  "co.unreachable": "读不了",
  "co.set_aside": "初筛挡下",
  "co.setAsideOn": "它的岗位都被你的硬约束放到了一边。",
  "co.followUp": "跟进",
  "co.noBoardYet": "还没找到它的招聘板",
  "co.unfollowNow": "不再关注",
  "co.tryAgain": "再试一次",
  "co.siteEdit": "它的招聘页地址…",
  "co.openCareers": "打开它的招聘页 ↗",
  "co.lookCareers": "去它的招聘页看看 ↗",
  "co.ats": "用什么 ATS",
  "ats.page": "招聘页",
  "ats.sitemap": "招聘页（站点地图）",
  "ats.lever_eu": "lever (EU)",
  "co.open": "在招",
  "co.openCount": "%1 条，其中匹配 %2",
  "co.sponsorship": "签证支持",
  "co.sponsorshipScope": "公司级 · 每个用户共用",
  "co.evidence": "证据",
  "co.basics": "基本情况",
  "co.size": "规模",
  "co.stage": "阶段",
  "co.hq": "总部",
  "co.inYourCity": "在你的城市",
  "co.workingLang": "工作语言",
  "co.remote": "远程政策",
  "co.unstated": "未说明",
  "co.openForYou": "在招 · 匹配",
  "co.of": "共",
  "co.openOnJobs": "匹配的那些排在岗位页。",
  "co.openArrow": "打开 →",
  "co.record": "我的记录",
  "co.noRecord": "你还没碰过这家。",
  "co.related": "相关公司",
  "co.notFollowed": "未跟",
  "co.deep": "深挖",
  "co.deepWhen": "有面试才做 · 研究于 %1",
  "co.deepWait": "有面试后我会深挖这家：产品、技术、团队、风险、值得问的。",
  "co.notResearched": "还没有调研这家公司。",
  "co.notResearchedBasics": "还没有调研这家公司；下面只是已登记的基本情况。",
  "co.product": "产品",
  "co.money": "怎么赚钱",
  "co.tech": "技术",
  "co.team": "团队",
  "co.risks": "风险",
  "co.ask": "值得问的",
  "co.thread": "公司线程",
  "co.note": "你的批注",
  "co.notePlaceholder": "只给你自己看…",
  "co.stated": "明说的",
  "co.guessed": "猜的",
  "co.noSuggest": "不会再替你跟进它。",
  "co.retryHint": "知道它真正的招聘页的话，填在上面再试一次。",
  "co.unreadable": "找到了，但它的招聘页上的岗位我读不到。",
  "co.relook": "我每周会再看一次；它用的系统一接上，当晚就看。",

  // the companies page (block 4b): the rail, the rows, the residue
  "co.added": "你加的",
  "co.ours": "替你跟进的",
  "co.oursPosting": "%1 · %2",
  "co.whyOurs": "为什么替你跟",
  "co.forYou": "匹配岗位数 %1",
  "co.noneForYou": "没有匹配的",
  "co.recOffer": "offer",
  "co.recInterviewing": "面试中",
  "co.recInvited": "已获邀请",
  "co.recApplied": "投过 %1 条",
  "co.recSilent": "沉默 %1 天",
  "co.recRejected": "已拒",
  "co.recQueued": "待投 %1 条",
  "co.recSaved": "收藏 %1 条",
  "co.declinedShort": "你说过不跟",
  "co.declinedOn": "你在 %1 说过不跟。",
  "co.unreachableShort": "我读不到它的岗位",
  "co.unreachableOn": "它的招聘页是 %1，我读不到岗位。",
  "co.tierAccount": "真给过需要签证的人面试或 offer",
  "co.tierPosting": "岗位明说",
  "co.tierResearch": "从公开资料推断",
  "co.tierUser": "你告诉我的",
  "co.hqHere": "%1 办公室（总部）",
  "co.restNote": "另外 %1 条不在这张卡上",
  "co.roundNo": "第 %1 轮",
  "co.search": "公司、行业、城市…",
  "co.searchIgnores": "搜索会看全部公司，不受「我的记录」与「跟踪」限制。",
  "co.rSaved": "收藏过",
  "co.rApplied": "投过",
  "co.rInterviewed": "面试过",
  "co.rOffer": "offer",
  "co.matchGroup": "匹配度",
  "co.matchHigh": "有高匹配在招",
  "co.matchMid": "有中匹配在招",
  "co.placeGroup": "地点",
  "co.industryGroup": "行业",
  "co.sizeStageGroup": "规模 · 阶段",
  "co.originGroup": "怎么来的",
  "co.trackGroup": "跟踪",
  "co.trackCounts": "计数是全部公司，不看「我的记录」。「初筛挡下」默认不勾。",
  "co.sortRelation": "按关系",
  "co.sortOpenings": "匹配岗位数",
  "co.sortName": "名字",
  "co.sortResearched": "最近研究",
  "co.clearAll": "清除所有筛选",
  "co.emptyList": "没有符合的公司。",
  "co.listTotal": "共 %1 家",
  "co.emptyCard": "选一家公司，这里是它的卡。",
  "rel.similar": "同类",
  "rel.upstream": "上游",
  "rel.downstream": "下游",
  "rel.rival": "竞品",
  "rel.same_group": "同集团",
  "covisa.yes": "会给",
  "covisa.likely": "可能会给",
  "covisa.unknown": "未知",
  "covisa.no": "不给",

  // jobs page (block 4a)
  "jobs.search": "公司、岗位、技能、城市…",
  "jobs.filters": "筛选",
  "jobs.status": "状态",
  "jobs.pending": "待看",
  "jobs.active": "进行中",
  "jobs.excluded": "已排除",
  "jobs.rejected": "已拒",
  "jobs.match": "待看里显示",
  "jobs.lv.high": "高",
  "jobs.lv.mid": "中",
  "jobs.lv.low": "低",
  "jobs.lv.unrated": "未评估",
  "jobs.unratedTip": "没有匹配档的岗位：初筛按你的硬约束放到一边的（原因在最下面「%1」组里），或者判断还没读到的。点它，这些岗位就会出现在列表里。",
  "jobs.gone": "已下架",
  "jobs.goneTip": "招聘方已撤下、你还没处理过的岗位，默认不在「待看」里。勾上，它们就回到列表里。你收藏、投递过的岗位下架了不会被藏起来，只会标出来。",
  "jobs.time": "时间",
  "jobs.fetched": "抓取时间",
  "jobs.posted": "发布时间",
  "jobs.today": "今天",
  "jobs.week": "本周",
  "jobs.month": "本月",
  "jobs.custom": "自定义",
  "jobs.from": "从",
  "jobs.to": "到",
  "jobs.go": "应用",
  "jobs.place": "地点",
  "jobs.placeInput": "城市或国家",
  "jobs.fromProfile": "你资料里的",
  "jobs.onsiteQ": "坐班",
  "jobs.moveQ": "搬不搬",
  "jobs.hard": "硬约束",
  "jobs.visaGroup": "签证",
  "jobs.langGroup": "工作语言",
  "jobs.payGroup": "薪资下限",
  "jobs.yours": "你填的：%1",
  "jobs.roles": "岗位",
  "jobs.rolesSearch": "找岗位名",
  "jobs.companyGroup": "公司",
  "jobs.setAside": "初筛排除（未判断）",
  "jobs.setAsideNote": "按你的硬约束，判断前就放到一边的岗位；和状态里你自己点的「已排除」不是一回事。",
  "jobs.companyInput": "公司名",
  "jobs.boards": "来源板子",
  "jobs.mineGroup": "我的",
  "jobs.pasted": "我带进来的",
  "jobs.relevelled": "我改过档的",
  "jobs.tailored": "简历改写",
  "jobs.closingWeek": "一周内截止",
  "jobs.closed": "已下架",
  "jobs.deadline": "截止",
  "jobs.sort": "排序",
  "sort.priority": "按优先级",
  "sort.match": "最匹配",
  "sort.posted": "发布时间",
  "sort.seen": "抓取时间",
  "sort.closing": "截止时间",
  "sort.company": "按公司",
  "jobs.clear": "全部取消",
  "pager.per": "%1 条/页",
  "pager.perBox": "每页条数",
  "pager.at": "第 %1 / %2 页",
  "pager.jumpBox": "跳到页码",
  "pager.jumpHint": "页码",
  "pager.prev": "上一页",
  "pager.next": "下一页",
  "pager.page": "第 %1 页",
  "jobs.new": "新",
  "jobs.brought": "你带来的",
  "jobs.closedTag": "已下架",
  "jobs.emptyActive": "还没有收藏或投出的岗位。",
  "jobs.emptyPending": "这里暂时没有待看的岗位。",
  "jobs.emptyHeldBack": "另有 %1 条被左侧「待看里显示」挡住了。",
  "jobs.emptyExcluded": "还没有跳过的。",
  "jobs.emptyRejected": "还没有被拒的。",
  "jobs.emptySearch": "我读过的里没有这个。",
  "jobs.emptyCard": "点左边一行，卡在这里。",
  "jobs.savedDays": "收藏了 %1 天",
  "jobs.sentDays": "投出 %1 天",
  "jobs.noReply": "还没回音",
  "jobs.closesOn": "%1 截止",
  "jobs.roundAt": "第 %1 轮 · %2",
  "jobs.timeToConfirm": "时间待确认",
  "jobs.passedLine": "你跳过 · %1 · %2",
  "jobs.rejectedLine": "被拒 · %1",
  "jobs.daysAgo": "%1 天前",
  "jobs.yesterday": "昨天",
  "jobs.more": "更多",
  "jobs.lowReason": "低匹配：%1",
  "jobs.unratedReason": "没读：%1",
  "render.pending": "有几处还没翻译好，先显示原文，翻好了会自己换上。",
  "render.tooLong": "有一部分太长，没送去翻译，按原文显示。",
  "render.refused": "有几处翻译不出来，按原文显示。",
  "render.mark": "原文",
  "render.why.pending": "这段还没翻译好，翻好了会自己换上。",
  "render.why.tooLong": "这段太长，没送去翻译。",
  "render.why.refused": "这段翻译不出来。",
  "step.resume": "读简历",
  "step.resume_content": "读每段经历的细节",
  "step.preferences": "理解工作偏好",
  "step.story": "完善画像",
  "step.profile": "写职业定位概述",
  "step.constraints_check": "核对条件",
  "step.select_sources": "选来源",
  "step.report": "写定位报告",
  "step.fetch": "抓取岗位",
  "step.seed": "为新方向找招聘板",
  "step.seed_pairs": "提前为新方向找招聘板",
  "step.topup": "补读自己的来源",
  "step.companies": "整理公司",
  "step.dedupe": "去重",
  "step.scope": "按条件筛选",
  "step.langs": "核对语言要求",
  "step.feedback": "提炼互动反馈",
  "step.judge": "判断",
  "step.apply_links": "找到岗位的申请页",
  "step.pairs_left": "为其他方向找岗位并判断",
  "step.translate_judgment": "翻译判断",
  "step.specs": "提取要点",
  "step.facts": "读取岗位里供硬条件筛选的信息",
  "step.sponsoring": "读取岗位对签证的说法",
  "step.reading": "读取岗位本身的要求",
  "step.hand_off": "给这个时区的人排上今天的搜索",
  "step.translate_posting": "翻译启事",
  "step.research": "查公司",
  "step.translate_company": "翻译公司资料",
  "step.retire": "清理过期岗位",
  "step.reasons": "归纳理由",
  "step.report_due": "看报告要不要重写",
  "step.brief": "写晨报",
  "step.forms": "读申请表",
  "step.questionnaire": "问卷",
  "step.find_similar": "找同类公司",
  "step.add_board": "接入招聘板",
  "step.mail_watch": "看回信",
  "step.linkedin_search": "在 LinkedIn 上找公司",
  "step.linkedin_resolve": "找公司的招聘板",
  "step.linkedin_sites": "查按名字没找到的公司官网",
  "step.aggregator_sites": "按用户要的国家找聚合源站点",
  "step.arbeitnow_boards": "从 arbeitnow 岗位找招聘板",
  "step.cdx_boards": "从公开网页索引找招聘板",
  "step.check_boards": "核实招聘板在哪招人",
  "step.admit_boards": "找到的招聘板按组合放进市场",
  "step.recheck_postings": "核实没有招聘板的岗位是否还在招",
  "step.follow_for_them": "替你跟进公司",
  "step.deep": "深挖",
  "step.accounts": "搜面经",
  "step.skills": "技能",
  "step.strategy": "策略",
  "step.backup": "备份",
  "step.retire_runs": "清理运行记录",
  "step.archive_spend": "归档模型花费明细",
  "step.fx_rates": "更新汇率",
  "step.retire_visits": "清理访问记录",
  "step.retire_auth": "清理登录记录",
  "step.trial_notices": "试用提醒",
  "step.unlikely_check": "抽查不对口档",
  "step.judge_all": "细看剩下的全部岗位",
  "step.take": "读贴的链接",
  "step.render": "翻译",
  "jobs.back": "← 岗位",
  "jobs.deepTitle": "判断",
  "jobs.endedNoReason": "没写理由",
  "shelf.target_country": "不在你的目标国家",
  "shelf.target_city": "不在你选的地点",
  "shelf.radius": "超出你的范围",
  "shelf.needs_permit": "国防 / 涉密类雇主",
  "shelf.work_lang": "要求本地语言",
  "shelf.direction": "不是你的方向",
  "shelf.salary_floor": "低于你的薪资下限",
  "shelf.remote": "远程要求不符",
  "shelf.start_by": "开始时间不符",
  "shelf.other": "其他硬约束",
  "shelf.defence": "国防或涉密岗位",
  "shelf.declined": "你说过不跟这家公司",
  "shelf.window": "超出你地区带的时间窗",
  "shelf.changed": "雇主把它改到了你不找的地方",
  "rejected.noTitle": "板子列出它时没有标题",
  "rejected.noDescription": "板子列出它时几乎没有正文",
  "jobs.visaRecord": "签证记录：%1 · 来自 %2 条",
  // 岗位 · 今天 (block 4a, PR2)
  "jobs.allToday": "查看今日所有推荐 →",
  "jobs.funnelThisWeek": "本周",
  "jobs.funnelLastWeek": "上周",
  "jobs.funnelHow": "按你所在时区的日历周算，周一开始，是此刻的数字。值得看：这一周新到、判为值得看的岗位，高、中、低匹配都算；一张张的卡只放今日的高匹配。我答过：最后一次状态变化落在这一周的岗位，推送里答的也算。",
  "jobs.funnelWorth": "值得看",
  "jobs.funnelAnswered": "我答过",
  "jobs.funnelMotion": "进展",
  "jobs.funnelCold": "还没有数字可报。首次搜索完成、你开始收藏和投递之后，这里就有东西了。",
  "jobs.funnelSaved": "收藏",
  "jobs.funnelApplied": "投",
  "jobs.funnelInterview": "面试",
  "jobs.funnelOffer": "offer",
  "jobs.paste.title": "贴一条",
  "jobs.paste.placeholder": "岗位链接",
  "jobs.paste.look": "看一下",
  "jobs.paste.hint": "已经有了 / 过滤掉了 / 现在补上。",
  "jobs.paste.have": "已经有了 →",
  "jobs.paste.added": "没发现，现在补上了。几分钟内就会读它。",
  "jobs.paste.bad": "看起来不是一个网址。",
  "jobs.paste.failed": "没贴上。",
  "jobs.paste.recent": "最近粘贴",
  "jobs.paste.state.new": "排队读",
  "jobs.paste.state.known": "已有",
  "jobs.paste.state.added": "已进列表",
  "jobs.paste.state.found": "找到了它的招聘板",
  "jobs.paste.state.unresolved": "没找到能读的板子",
  "jobs.paste.state.failed": "页读不了",
  "jobs.paste.state.declined": "你说不跟",
  "shelf.judge": "判断没过",
  // the extension's panel: served with /api/ext/seen as `labels`, so the extension carries no second copy (BLUEPRINT §6); %1 is the count or the name
  "ext.shown": "我给过你 —— 在列表里。",
  "ext.judged": "我读过它，判断为不值得占用你的时间。",
  "ext.filtered": "我看到了，但在花钱判断之前就过滤掉了。",
  "ext.shelved": "我有它，但按现在的范围先搁置了。",
  "ext.unread": "我有它 —— 还没轮到读它。",
  "ext.unseen": "我没见过它。这是我的漏洞。",
  "ext.employerLede": "这是一家公司主页。要我盯它的招聘吗？",
  "ext.find": "找找这家公司",
  "ext.findNamed": "去找 %1 自己的招聘板",
  "ext.keeping": "正在收下…",
  "ext.asking": "正在问服务器…",
  "ext.timeout": "服务器一直没回答。",
  "ext.learned": "记下了 %1 个答案 —— 下一份表不用再填。",
  "ext.ask": "问点什么…",
  "ext.send": "发送",
  "ext.thinking": "在想…",
  "ext.thought": "它是怎么想的",
  "ext.chatHere": "问它",
  "ext.tailor": "为这个岗位改简历",
  "ext.tailoring": "正在准备…",
  "ext.tailorOpened": "改写在门户里，已经在新标签页打开。",
  "ext.tailorBlocked": "改写在门户里，浏览器没让我打开新标签页。",
  "ext.tailorOpen": "打开改写页 →",
  "ext.tailorKept": "这个岗位已收藏进你的清单。",
  "ext.tailorNotPosting": "这一页读不出招聘要求，没法按它改简历。",
  "ext.tailorNoCv": "你的简历还要再读一遍，才能逐行改写。",
  "ext.tailorToMe": "去「我的资料」看看 →",
  "ext.tailorFail": "没能开始改写。",
  "ext.keptNew": "收下了。判断一分钟内会出现在门户里，这里不用等。",
  "ext.undo": "撤销",
  "ext.keptSame": "这条我已经有了 —— %1 的板子上。已经记在你名下。",
  "ext.keptFail": "没收下 —— 这一页读不出是哪个岗位。",
  "ext.boardOnly": "这是招聘网站的页面，我不从这里抓，也读不出是哪家公司。\n打开这家公司的官网再点一次插件图标就行。",
  "ext.save": "收藏",
  "ext.pass": "不适合我",
  "ext.saved": "已收藏",
  "ext.passed": "已排除",
  "ext.queued": "已加入待投",
  "ext.queueUp": "加入待投",
  "ext.markApplied": "已投",
  "ext.appliedDone": "已投递",
  "ext.interviewRequested": "邀请面试",
  "ext.interviewing": "面试中",
  "ext.offer": "拿到 offer",
  "ext.rejected": "被拒",
  "ext.http409": "这个岗位已经在面试流程里了，要改去 jobs 页面改。",
  "ext.http402": "试用结束了 —— 到门户的订阅页继续。",
  "ext.why": "为什么是你",
  "ext.pause": "我的顾虑",
  "ext.noToken": "还没设置钥匙 —— 点插件图标填一下。",
  "ext.offline": "连不上服务器。",
  "ext.failed": "没接上。再试一次？",
  "ext.badToken": "这把密钥已失效 —— 在门户「我的资料 → 设置」里生成新密钥，粘贴到插件设置里。",
  "ext.openSettings": "打开插件设置",
  "ext.notJson": "服务器返回的不是数据 —— 多半是要重新登录。",
  "ext.dock": "停靠为右侧边栏（更大）",
  "ext.undock": "还原为小浮层",
  "ext.formHere": "这一页有申请表：%1 个字段",
  "ext.known": "我知道 %1 个",
  "ext.needYou": "%1 个需要你",
  "ext.yoursAlone": "%1 个只能你自己填（声明、自述）",
  "ext.menus": "%1 个下拉菜单，你点一下就好",
  "ext.doFill": "填上我知道的",
  "ext.answer": "回答那 %1 个",
  "ext.keep": "记下来",
  "ext.back": "返回",
  "ext.retry": "再试一次",
  "ext.retrying": "再试…",
  "ext.left": "还有 %1 个",
  "ext.skip": "跳过",
  "ext.filled": "填好 %1 个。",
  "ext.refused": "%1 个没填 —— 选项对不上，留给你。",
  "ext.askLede": "答一次，以后每一份表都不用再答。",
  "ext.yourWords": "%1 个要你自己写",
  "ext.confirmName": "申请表上的姓名还没确认 —— 去「我的资料 · 设置 · 浏览器插件」确认一次",
  "ext.confirmHome": "申请表上的居住地还没确认 —— 去「我的资料 · 设置 · 浏览器插件」确认一次",
  "ext.confirmPhone": "申请表上的电话还没确认 —— 去「我的资料 · 设置 · 浏览器插件」确认一次",
  "ext.confirmSalary": "期望薪资没填（不会拿最低年薪代替）—— 去「我的资料 · 岗位搜索设置」填目标年薪，或直接填在表上，下次会记住",
  "ext.confirmSalaryCurrency": "期望薪资没填：表上要的币种和你设的目标年薪不同，没替你换算 —— 请直接填在表上，下次会记住",
  "ext.assistTitle": "让它帮你填这一个",
  "ext.singleFilled": "填好了。",
  "ext.singleUnknown": "这个我还不知道 —— 留给你自己填。",
  "ext.singleFail": "没填成。",
  "ext.improve": "和 AI 一起改",
  "ext.draftLede": "和 AI 一起把这一题写出来，满意后点「填入」。",
  "ext.draftAsk": "说说要点或想怎么改…",
  "ext.insert": "填入",
  "ext.draftEmpty": "草稿还是空的 —— 先和 AI 聊出内容。",
  "ext.looking": "正在找它的招聘板，通常要十几秒…",
  "ext.finding": "还在找这家公司的招聘板。找完，结果会在门户 · 公司页上。",
  "ext.foundFollowing": "在跟 %1 了：它的岗位会进你的每日搜索。",
  "ext.foundDeclined": "%1 你之前说过不跟。要改主意，在门户里改。",
  "ext.foundUnread": "%1 的招聘页用的是 %2，我们还读不了。已记在门户 · 公司页。",
  "ext.foundNone": "找不到 %1 能读的招聘板。已记在门户 · 公司页。",
  "ext.foundNothing": "找不到这家公司能读的招聘板。",
  "ext.foundFailed": "这一页读不了，找不到这家公司的招聘板。",
  "ext.findOpen": "在门户里看 →",
  "ext.employerSite": "这是 %1 的网站。",
  "ext.employerUnread": "%1 的招聘板我们读不了——看到想要的岗位就在这里收下，会记到它的卡上。",
  "ext.autoOpen": "以后在它的网站上自动打开",
  "ext.autoOpenAsked": "在刚打开的设置页里点「允许」。",
  "ext.viaCompanyPage": "这一页上没有 %1 的官网。打开它的 LinkedIn 公司页，再点一次插件图标，我能从那里读到官网。",
  "ext.companyPageOpen": "打开公司页 →",
  "ext.noSite": "%1 的 LinkedIn 页面上没有写官网，我找不到它自己的招聘板。",
  "ext.noSitePage": "这一页上没写 %1 的官网，我找不到它自己的招聘板。",
  // ---- 面试页（块 4c）：日历、两个列表、全宽阅读态
  "iv.prevMonth": "上个月",
  "iv.nextMonth": "下个月",
  "iv.prevWeek": "上一周",
  "iv.nextWeek": "下一周",
  "iv.today": "今天",
  "iv.week": "周",
  "iv.month": "月",
  "iv.markInterview": "面试",
  "iv.markUnconfirmed": "待确认",
  "iv.markDeadline": "作业截止",
  "iv.kind.screen": "初筛",
  "iv.kind.technical": "技术面",
  "iv.kind.system": "系统设计",
  "iv.kind.behavioural": "行为面",
  "iv.kind.take_home": "带回家作业",
  "iv.kind.written": "笔试",
  "iv.kind.onsite": "现场面",
  "iv.kind.final": "终面",
  "iv.kind.other": "其他",
  "iv.format.video": "视频",
  "iv.format.phone": "电话",
  "iv.format.onsite": "到场",
  "iv.format.async": "异步",
  "iv.roundNo": "第 %1 轮",
  "iv.due": "截止",
  "iv.unscheduled": "时间待定",
  "iv.theirTime": "对方时间 %1",
  "iv.toConfirm": "确认时间",
  "iv.noTimeYet": "还没定时间",
  "iv.cancelled": "已取消",
  "iv.roundDone": "已面完",
  "iv.timeNo": "这个时间不行",
  "iv.upcoming": "即将",
  "iv.allDays": "看全部",
  "iv.addRound": "记一场",
  "iv.noneAhead": "没有安排中的面试。",
  "iv.ended": "已结束",
  "iv.noneEnded": "还没有结束的面试。",
  "iv.nothing": "还没有面试。岗位标成「邀请面试」后，这里会出现。",
  "iv.back": "← 面试",
  "iv.joinLink": "会议链接 ↗",
  "iv.packet.deep": "深挖",
  "iv.packet.accounts": "面经",
  "iv.packet.skills": "技能",
  "iv.packet.strategy": "策略",
  "iv.packetWhat.deep": "这家公司和这个岗位在做什么",
  "iv.packetWhat.accounts": "别人在这家面过什么",
  "iv.packetWhat.skills": "他们会考什么、你有多少",
  "iv.packetWhat.strategy": "该强调什么、会被质疑什么",
  "iv.reason.none_found": "没有搜到这家公司的面经。",
  "iv.reason.namesake": "搜到了面经，但可能是同名的另一家公司，所以没有采用。",
  "iv.reason.no_skills_named": "还没读出这个岗位要哪些技能，没法做技能对照。",
  "iv.reason.no_web": "这次没有联网搜索。",
  "iv.reason.search_unavailable": "搜索服务出错了，这份没搜成。",
  "iv.reason.model_unavailable": "生成服务出错了，这份没有写完整。",
  "iv.reason.budget": "这次准备的预算用完了，这份没做完。",
  "iv.reason.rejected": "有写出来的内容没通过检查，没有保存。",
  "iv.reason.untranslated": "这份翻译成你的语言没成功。",
  "iv.reason.needs_cv": "还没读到你简历里的技能，没法做技能对照。",
  "iv.reason.needs_judgment": "这条岗位还没评估过，策略没法写。",
  "iv.reason.needs_company": "还没有这家公司的资料，岗位深挖没法做。",
  "iv.reason.failed": "这一份没有完整做完。",
  "iv.noneFoundGuess": "没有搜到这家公司的面经，下面的考点是按岗位推断的。",
  "iv.namesakeGuess": "搜到的面经可能是同名的另一家公司，下面的考点是按岗位推断的。",
  "iv.notStarted": "还没开始准备。",
  "iv.queued": "准备排在队里，跑完了四份材料会出现在这里。",
  "iv.running": "正在准备，几分钟后回来看。",
  "iv.ranEmpty": "准备跑过了，四份材料一份都没出来。",
  "iv.strategy.they_value": "他们最看重",
  "iv.strategy.emphasise": "该强调",
  "iv.strategy.will_be_questioned": "会被质疑",
  "iv.strategy.ask_them": "该问他们",
  "iv.strategy.sponsorship": "签证怎么开口",
  "iv.skillCount": "%1 项 · 核心 %2 / 次要 %3",
  "iv.by.cv": "简历直判",
  "iv.by.model": "模型判",
  "iv.unverified": "未验证",
  "iv.source.firsthand": "一手",
  "iv.source.employer": "雇主",
  "iv.source.aggregator": "聚合",
  "iv.source.other": "其他",
  "iv.sourceLegend": "一手 = 面过的人写的；雇主 = 公司自己说的流程；聚合 = 第三方汇总；推断 = 我按岗位猜的。",
  "iv.snippetOnly": "只读到搜索摘要",
  "iv.inferredWhole": "整份是推断",
  "iv.inferred": "推断会考什么",
  "iv.inferredCaveat": "这是我按启事和相关材料推断的，不是谁真面过的记录。",
  "iv.deep": "深挖",
  "iv.team": "团队",
  "iv.problem": "在做什么",
  "iv.stack": "技术栈",
  "iv.business": "怎么赚钱",
  "iv.risks": "风险",
  "iv.process": "招聘流程",
  "iv.angles": "面试可以聊的点",
  "iv.news": "近期动态",
  "iv.viewResearch": "查看调研",
  "iv.hideResearch": "收起调研",
  "iv.sourceDetails": "检索与出处：",
  "iv.roleSources": "岗位资料：",
  "iv.companySources": "公司资料：",
  "iv.notePrompt": "问了什么 / 答得怎样 / 下一步",
  "iv.notePlaceholder": "面完记一句…",
  "iv.share": "把这一轮的笔记匿名贡献给共享面经：去掉你的身份，只留问题与流程。",
  "iv.saveNote": "保存",
  "iv.markDone": "面完了",
  "iv.saveFailed": "没存成：%1",
  "iv.addRoundN": "＋ 记下第 %1 轮",
  "iv.fieldKind": "类型",
  "iv.fieldWhen": "时间",
  "iv.fieldMinutes": "时长（分钟）",
  "iv.fieldFormat": "形式",
  "iv.fieldLink": "会议链接",
  "iv.fieldLang": "面试语言",
  "iv.fieldWho": "面试官",
  "iv.saveRound": "记下",
  "iv.cancel": "取消",
  "round.title": "收到面试邀请：记下这一轮",
  "round.titleNext": "开始面试了：记下这一轮",
  "round.titleTime": "填上面试时间",
  "round.titleAdd": "记下一轮面试",
  "round.later": "稍后再填",
  "round.laterNote": "「稍后再填」也会改状态；时间之后可以在卡片上补。",
  "round.needWhen": "要先填上时间",
  "card.noTime": "还没填面试时间，日历上还看不到这场面试。",
  "card.addTime": "填上时间",

  "strength.hard": "硬",
  "strength.strong": "强",
  "strength.mild": "弱",
  "profile.sec.positions": "工作经历",
  "profile.sec.skills": "技能",
  "profile.sec.about": "简介",
  "profile.sec.education": "教育",
  "profile.sec.publication": "出版物",
  "profile.sec.certification": "证书",
  "profile.sec.award": "奖项",
  "profile.sec.project": "项目",
  "profile.sec.other": "其他",
  // ---- 我的资料（块 4d）：关于我 / 规则 / 设置 与定位报告
  "me.tab.about": "关于我",
  "me.tab.rules": "岗位搜索设置",
  "me.tab.settings": "设置",
  "me.ask.one": "等你确认的一条",
  "me.ask.rule": "这条会改变筛选规则，要你点头",
  "me.ask.shelve": "要不要把这一类放到一边",
  "me.ask.report": "定位报告写好了",
  "me.ask.generic": "有一条等你回答。",
  "me.ask.placeholder": "直接写答案…",
  "me.ask.send": "答了",
  "me.name.title": "申请表上的名字",
  "me.name.full": "全名",
  "me.name.first": "名",
  "me.name.last": "姓",
  "me.name.unconfirmed": "还没确认。确认前，插件不会自动填写姓名。",
  "me.name.confirmed": "已确认，申请表按这个填。",
  "me.name.save": "确认",
  "me.cv.title": "简历",
  "me.cv.upload": "上传",
  "me.cv.replace": "更换",
  "me.cv.sending": "上传中…",
  "me.cv.tooBig": "超过 10MB 了",
  "me.cv.same": "这就是已经传过的那份文件，没有变化。",
  "me.cv.none": "还没有简历。先交一份，能从里面读出来的我都不问你。",
  "me.cv.meta": "%1 KB · %2 上传 · %3 段经历 · %4 个学位 · %5 项技能",
  "me.cv.inProgress": "在读",
  "me.cv.unread": "还没读过它——已经排上了，几分钟内就读。",
  "me.cv.detailsPending": "经历和学历已经读出；每段经历做了什么、其余技能还在读。",
  "me.cv.detailsRefused": "经历和学历已经读出，但每段经历做了什么、其余技能没能读出来——换一份简历会重新读。",
  "me.cv.failed": "这份文件读不了：%1。另存一份再传，或者传 PDF。",
  "me.cv.why.no_text": "里面没有能读出的文字，多半是扫描件或图片",
  "me.cv.why.locked": "它设了密码，或是老式的 .doc 格式",
  "me.cv.why.other_type": "它不是 PDF 或 Word 文档",
  "me.cv.why.damaged": "文件已损坏",
  "me.cv.why.unreadable": "读取时出了错",
  // The file read; what was read out of it did not (#377). A different
  // sentence, because 「另存一份再传」 is not the thing to do about it.
  "me.cv.refused": "这份简历没能读成：%1，所以没有记下来。再传一次同一份文件，我会重新读；也可以换 Word（.docx）版本试试。",
  "me.cv.why.refused": "读出来的内容都没通过检查",
  "me.cv.download": "下载原件",
  "me.cv.earlier": "以前的简历",
  "me.cv.earlierOn": "%1 上传",
  "me.cv.earlierMore": "另外 %1 份",
  "me.cv.fold": "看我读出来的经历与技能",
  "me.cv.wrong": "读错了？换一份简历，或者在对话里直接告诉我（比如「Go 只是够用」），我会记成画像的一版。这里不改，是因为改在这儿就成了简历里没有的说法。",
  "me.doc.profile": "画像 · 第 %1 版",
  "me.doc.at": "%1 改过",
  "me.doc.never": "还没写过",
  "me.doc.emptyProfile": "还没有画像。交一份简历，或者在对话里跟我说说你在找什么。",
  "me.doc.ownOnly": "这个框里是你和 agent 聊出来的那部分画像。下面这些是自动写的，这里改不了：",
  "me.doc.overviewNote": "「职业定位与背景概述」由系统从你的简历、设置和回答提炼，重新上传简历时会重写。",
  "me.doc.notesNote": "「地点与办公方式偏好」是岗位搜索设置的总结，跟着设置自动同步。",
  "profile.block.profile_overview": "职业定位与背景概述",
  "profile.block.job_search_notes": "地点与办公方式偏好",
  "profile.block.learned": "从反馈学到的",
  "me.doc.notesLink": "去设置里改",
  "me.doc.edit": "编辑",
  "me.doc.save": "保存",
  "me.doc.conflict": "如果你这一段在这期间被别处改过（比如对话里记下了一句），保存会被拒，刷新后再改。系统写的部分变了不影响保存。",
  "me.src.web": "你改的",
  "me.src.chat": "从对话里",
  "me.src.resume": "从简历",
  "me.src.criterion": "你点头的规则",
  "me.src.constraint": "跟着硬约束改的",
  "me.src.question": "你回答的问题",
  "me.src.feedback": "从你的互动反馈里",
  "me.src.unknown": "来源不详",
  "me.hc.permitNo": "不需要",
  "me.hc.direction": "目标方向",
  "me.hc.hint.salary_floor": "80k",
  "me.report.title": "市场定位报告",
  "me.report.locked": "要生成它，我还需要：",
  "me.report.need.resume": "一份简历",
  "me.report.need.profile": "一份画像",
  "me.report.need.market": "本月的市场数据",
  "me.report.need.questions": "%1 个还没答的问题",
  "me.report.lockedHint": "上面的清单齐了，按钮才亮。",
  "me.report.ready": "输入齐了：画像 v%1 · 市场数据 %2",
  "me.report.making": "报告生成中…",
  "me.report.retry": "重新生成",
  "me.report.failedHint": "这一次没写成。上面说了是什么原因；按一次再试。",
  "me.report.failed.money": "预算用完了，这一份没写成。加了额度再试。",
  "me.report.failed.refused": "模型写出来的两次都没过检查（引用对不上或者结构不对）。再试一次通常就过了。",
  "me.report.failed.timeout": "写了十五分钟还没写完，被掐掉了。再试一次。",
  "me.report.failed.provider": "模型那边出错了，没写成。再试一次。",
  "me.report.failed.unknown": "这一份没写完，也没留下原因——多半是跑到一半被中断了。再试一次。",
  "me.report.writingHint": "已经排上队了。写完这里会自己变过来。",
  "me.report.make": "写一份",
  "me.report.again": "重新写",
  "me.report.open": "打开报告 →",
  "me.report.back": "← 关于我",
  "me.report.written": "%1 写的 · 一共 %2 份",
  "me.report.same": "自那以后，写它用到的都没变。",
  "me.report.moved": "变过的：%1",
  "me.report.versions": "以前的 %1 份",
  "me.report.from": "画像 v%1 · %2",
  "me.report.head": "市场定位报告 · %1",
  "me.report.empty": "这份报告是空的。",
  "me.report.howTitle": "这份报告怎么来的",
  "me.report.how": "简历和画像是版本：改了一句才算变。规则：加、撤、改一条才算变。市场看周和推荐的量级：换了一周、推荐数翻倍或减半才算变；首搜结束时会自动重写一次。",
  "me.report.market.searching": "首搜结束前",
  "me.report.market.week": "%1 那周",
  "me.report.input.resume": "简历",
  "me.report.input.profile": "画像",
  "me.report.input.market": "市场数据",
  "me.report.stillAsking": "还有没答的问题——答了下一份会更准。",
  "me.report.s.summary": "一句话",
  "me.report.s.strengths": "你的优势",
  "me.report.s.positioning": "该瞄准什么",
  "me.report.s.market": "市场在要什么",
  "me.report.s.gaps": "你的缺口",
  "me.report.s.next_steps": "这周能做的",
  "me.w.title": "我怎么排序",
  "me.w.lede": "点筛选会抬高对应因素的权重（%1）。做决定、写理由不改这里的权重，它们会变成这一页下面的规则。其余因素用默认值，或你自己在下面设的。",
  "me.w.edit": "调整",
  "me.w.save": "保存",
  "me.w.reset": "恢复默认",
  "me.w.saveHint": "保存后列表马上按新权重排。",
  "me.w.f.stack": "技术栈",
  "me.w.f.fit": "合不合适",
  "me.w.f.where": "地点可不可行",
  "me.w.f.reach": "多少人能投",
  "me.w.f.age": "发布多久",
  "me.w.f.chance": "回音的机会",
  "me.w.f.level": "级别",
  "me.w.f.visa": "签证",
  "me.w.f.stability": "公司稳不稳",
  "me.w.f.pay": "薪资",
  "me.w.why.default": "默认",
  "me.w.why.attention": "你筛过 %1 次",
  "me.w.why.own": "你设的",
  "me.w.why.stack": "乘数，不可调",
  "me.learned.title": "最近学到的",
  "me.learned.where": "点头的会成为你的一条规则，判断层逐字读它。",
  "me.rules.n": "%1 条",
  "me.rules.none": "还没有点过头的规则。",
  "me.rules.title": "规则",
  "me.rules.lede": "现在生效的规则，判断层逐字读它们。撤回一条，下次运行就不再按它判；「硬」的会直接排除岗位。",
  "me.rules.add": "添加规则",
  "me.rules.what": "写一条规则，一句话",
  "me.rules.retire": "撤回",
  "me.rules.group.auto": "自动生效的规则",
  "me.rules.group.confirmed": "你点过头的",
  "me.report.input.criteria": "规则",
  "me.doc.rules.count": "生效中的规则 %1 条",
  "me.doc.rules.quoted": "（%1）",
  "me.doc.rules.item": "%1：「%2」",
  "me.doc.rules.sep": "；",
  "me.doc.rules.more": "另 %1 条",
  "me.doc.rules.link": "岗位搜索设置 → 规则",
  "me.doc.learnedNote": "「从反馈学到的」是每晚从你的 pass 原因、评论和申请表答案里学到的事实。",
  "me.src.rules": "规则变了",
  "me.doc.correctionHeading": "简历事实更正",
  "me.reasons.title": "你拒绝时用的理由",
  "me.reasons.lede": "按用得多少排；点得多的排在卡片前面。",
  "me.reasons.uses": "用过 %1 次",
  "me.reasons.teaser": "%1 条理由 · 最常用：%2",
  "me.reasons.teaserNone": "%1 条理由，还都没用过",
  "me.set.on": "开着",
  "me.set.off": "已关",
  "me.set.turnOn": "打开",
  "me.set.turnOff": "关掉",
  "me.set.switchFailed": "没改成，请再试一次。",
  "wait.working": "正在保存…",
  "wait.failed": "没改成，请再试一次。",
  "me.set.ch.title": "通知",
  "me.set.ch.lede": "邮件只发晨报和试用提醒；Slack 另带问题、报告、线索和面试准备。其余的在网页上看。关掉一个地址就不再往它发。",
  "me.set.ch.none": "还没有能找到你的地址：现在什么都发不出去。",
  "me.set.ch.kind.email": "邮件",
  "me.set.ch.kind.slack": "Slack",
  "me.set.ch.kind.whatsapp": "WhatsApp",
  "me.set.ch.kind.telegram": "Telegram",
  "me.set.ch.since": "加于 %1",
  "me.set.ch.quiet": "静默 %1–%2",
  "me.set.ch.events": "只推：%1",
  "me.set.ch.remove": "断开连接",
  "me.set.ch.removeTitle": "断开「%1」？",
  "me.set.ch.removeAsk": "断开后，这里的推送和对话都会停；要恢复得重新连接。",
  "me.set.ch.removeAskPush": "断开后，这里不再收到推送；要恢复得重新连接。",
  "me.set.ch.push": "推送",
  "me.set.ch.loginEmail": "这是你的登录邮箱，断开不了——换地址在「账户」里改，不想收信就关掉右边的开关。",
  "me.set.days.title": "晨报日期",
  "me.set.days.hint": "选中的那几天早上各发一封；这几天也是判断结果攒够一批就送出的日子。",
  "me.set.days.none": "一天都没选：现在不会发晨报。",
  "me.set.auto.title": "自动化",
  "me.set.auto.lede": "只有这两个开关有人读：它们决定你标了一个状态之后，要不要自动接着跑一段活。",
  "me.set.auto.similar": "标「已投」后去找相似的",
  "me.set.auto.what.similar": "开着时，你把一条标成已投，就排一段 S4：找相似的公司与岗位，把你接受的线索加成新板子。关掉就只是记下你投了。",
  "me.set.auto.prep": "标「约面试」后自动准备材料",
  "me.set.auto.what.prep": "开着时，你把一条岗位标成「面试邀请」，就开始准备四份材料：公司与岗位深挖、面经、技能对照和一份策略。关掉就不准备，也没有别的地方能另外发起。",
  "me.set.auto.onlyTwo": "就这两个。读邮箱、夜间任务时间、自动填表的档位、海投——都还没做，所以这里不画成开关。",
  "me.set.ext.title": "浏览器插件",
  "me.set.ext.identity": "申请表资料",
  "me.set.ext.identityHint": "以下是从已有资料读出的建议。请按申请表实际使用的写法分别确认姓名、居住地和电话；这里只影响插件填表。",
  "me.set.ext.residence": "居住地",
  "me.set.ext.place": "城市、国家或完整居住地",
  "me.set.ext.phone": "电话",
  "me.set.ext.number": "电话号码",
  "me.set.ext.confirmed": "已确认，申请表按这里填写。",
  "me.set.ext.unconfirmed": "还没确认，插件暂不自动填写这一项。",
  "me.set.ext.noSource": "没有现成资料，请填写后确认",
  "me.set.ext.source.said": "来自你填写的资料",
  "me.set.ext.source.resume": "来自当前简历",
  "me.set.ext.source.account": "来自账号",
  "me.set.ext.source.verified": "来自已验证的号码",
  "me.set.ext.direct": "邮箱和链接按现有资料直接填写，无需再次确认。",
  "me.set.ext.email": "邮箱",
  "me.set.ext.website": "网站",
  "me.set.ext.none": "还没有密钥：插件现在连不上。",
  "me.set.ext.mint": "生成密钥",
  "me.set.ext.revoke": "作废",
  "me.set.ext.on": "已连接 · 这把密钥生成于 %1",
  "me.set.ext.replace": "换一把新密钥",
  "me.set.ext.one": "一个账号只有一把密钥。在几个浏览器或几台电脑上用插件，就在生成时把同一把粘贴到每一个里；换了新的，每一处都要换。",
  "me.key.title": "你的插件密钥",
  "me.key.titleReplace": "换一把新密钥？",
  "me.key.replaceBody": "现在这把会立即失效，装了插件的每个浏览器都要换成新密钥。",
  "me.key.replaceYes": "换一把",
  "me.key.cancel": "取消",
  "me.key.making": "正在生成…",
  "me.key.failed": "没生成成功，再试一次。",
  "me.key.again": "再试一次",
  "me.key.once": "只显示这一次，现在就复制走——关掉就看不到了。",
  "me.key.copy": "复制",
  "me.key.copied": "已复制",
  "me.key.copyByHand": "已经全选，按 ⌘C / Ctrl+C 复制。",
  "me.key.next": "在插件里换上这把：插件面板的「打开插件设置」，或右键插件图标 → 选项，粘贴后保存。",
  "me.key.done": "完成",
  "me.set.ext.shownOnce": "密钥只在生成的那一刻显示一次，这里存的是它的哈希，找不回来——丢了就换一把新的。",
  "me.set.acct.title": "账户",
  "me.set.acct.email": "登录邮箱",
  "me.set.acct.noEmail": "没记邮箱",
  "me.set.acct.since": "注册于 %1",
  "me.set.acct.lang": "邮件与推送语言",
  "me.set.acct.langNote": "邮件、Slack 和 WhatsApp 上发给你的消息用这个语言写；你的画像、规则和定位报告也按这个语言保存。",
  "me.set.acct.tz": "时区",
  "me.set.acct.tzHint": "Europe/Berlin",
  "me.set.acct.tzNote": "页面上的每个日期、晨报算的是哪一天，都按这个时区。",
  "me.set.acct.save": "保存",
  "me.set.acct.delete": "要删除账号和数据，写信给我们：",
  "me.set.acct.deleteSubject": "删除账号",
  "me.set.export.title": "导出",
  "me.set.export.short": "下载你的个人资料。",
  "me.set.export.what": "下载一个 zip：你上传的简历原件、对话里发的附件，以及一份整理好的文档——账号、画像、搜索设置和规则、对话、回答过的问题、对岗位的决定和面试——另附机器可读的 data.json。我们收集的岗位和公司资料、系统记录不在里面，需要的话可以写信索取。",
  "me.set.export.go": "导出我的数据",
  "mine.title": "我在 Counting 的数据",
  "mine.toc": "目录",
  "mine.sec.about": "说明",
  "mine.sec.account": "账号",
  "mine.sec.profile": "画像",
  "mine.sec.resume": "上传的简历",
  "mine.sec.search": "搜索设置和规则",
  "mine.sec.threads": "和 agent 的对话",
  "mine.sec.questions": "回答过的问题",
  "mine.sec.jobs": "对岗位的决定",
  "mine.sec.interviews": "面试",
  "mine.about.intro": "这份文件是你在 Counting 上提供的内容，以及直接关于你本人的内容。同样的内容另有一份机器可读的 data.json，和这份文件放在同一个包里。",
  "mine.about.files": "包里还有：resume/ 是你上传的简历原件，attachments/ 是你在对话里发的附件。",
  "mine.about.at": "导出时间",
  "mine.about.tz": "时间按这个时区写",
  "mine.about.left": "没有放进来的",
  "mine.left.jobs": "我们收集或整理的岗位和公司资料，以及对它们的判断和评分",
  "mine.left.system": "系统运行记录（任务、用量、访问记录等）",
  "mine.left.billing": "付费记录",
  "mine.left.tailor": "改写过的简历",
  "mine.about.ask": "需要这些数据的话，写信到 %1 索取。",
  "mine.about.askAnyway": "需要这些数据的话，可以写信给我们索取。",
  "mine.about.lost": "没能取出、没有放进包里的文件",
  "mine.file.missing": "文件不在了",
  "mine.file.changed": "文件和当初存下的不一致",
  "mine.file.gone": "只留了记录，原件已删",
  "mine.acct.email": "邮箱",
  "mine.acct.phone": "手机号",
  "mine.acct.lang": "界面语言",
  "mine.acct.tz": "时区",
  "mine.acct.since": "注册日期",
  "mine.profile.doc": "关于我",
  "mine.profile.docAt": "这份文档更新于",
  "mine.profile.experience": "经历",
  "mine.profile.education": "教育",
  "mine.profile.skills": "技能",
  "mine.profile.items": "其他",
  "mine.exp.period": "时间",
  "mine.exp.location": "地点",
  "mine.exp.summary": "概述",
  "mine.exp.claims": "要点",
  "mine.exp.stack": "技术栈",
  "mine.period.now": "至今",
  "mine.edu.degree": "学位",
  "mine.edu.field": "专业",
  "mine.skill.kind.language": "编程语言",
  "mine.skill.kind.framework": "框架",
  "mine.skill.kind.tool": "工具",
  "mine.skill.kind.platform": "平台",
  "mine.skill.kind.domain": "领域",
  "mine.skill.kind.practice": "方法与实践",
  "mine.skill.kind.human_language": "语言",
  "mine.skill.kind.other": "其他",
  "mine.skill.level.native": "母语",
  "mine.skill.level.proficient": "熟练",
  "mine.skill.level.working": "能用",
  "mine.skill.level.familiar": "了解",
  "mine.item.kind": "类别",
  "mine.item.publication": "发表",
  "mine.item.certification": "证书",
  "mine.item.award": "奖项",
  "mine.item.project": "项目",
  "mine.item.other": "其他",
  "mine.item.detail": "说明",
  "mine.item.date": "日期",
  "mine.cv.file": "文件",
  "mine.cv.first": "第一次上传",
  "mine.cv.last": "最近一次上传",
  "mine.cv.current": "当前使用",
  "mine.yes": "是",
  "mine.no": "否",
  "mine.search.settings": "搜索设置",
  "mine.search.rules": "已定的规则",
  "mine.search.reasons": "你自己的跳过理由",
  "mine.search.switches": "开关",
  "mine.thread.kind": "类型",
  "mine.thread.started": "开始于",
  "mine.thread.files": "附件",
  "mine.thread.unsent": "没有随消息发出的附件",
  "mine.who.you": "你",
  "mine.who.agent": "agent",
  "mine.q.asked": "agent 问过的",
  "mine.q.answer": "你的回答",
  "mine.q.at": "回答于",
  "mine.q.forms": "表单里的答案",
  "mine.q.value": "答案",
  "mine.job.unknown": "岗位信息已不在",
  "mine.job.reasons": "理由",
  "mine.job.note": "备注",
  "mine.job.sent": "从扩展发来的页面",
  "mine.sent.url": "网址",
  "mine.sent.company": "公司",
  "mine.sent.state": "结果",
  "mine.sent.at": "发来于",
  "mine.iv.rounds": "轮次",
  "mine.iv.time": "时间",
  "mine.iv.minutes": "时长（分钟）",
  "mine.iv.format": "形式",
  "mine.iv.interviewers": "面试官",
  "mine.iv.link": "会议链接",
  "mine.iv.notes": "备注",
  "mine.iv.state": "状态",
  "mine.round.proposed": "时间待确认",
  "mine.round.confirmed": "已约定",
  "mine.iv.sponsorSay": "怎么开口",
  "mine.iv.sponsorConfirm": "要确认的",
  "mine.iv.notCovered": "材料没覆盖到的",
  "mine.iv.prepare": "要准备的",
  "mine.iv.questions": "可能被问到的",
  "mine.iv.skillNote": "说明",
  "me.set.end.title": "搜索结束 / 重新开始",
  "me.set.end.ended": "已在 %1 记成「搜索结束」。",
  "me.set.end.end": "我找到工作了",
  "me.set.end.restart": "重新开始找",
  "me.set.end.honest": "「我找到工作了」让它停止给你找岗位；「重新开始找」让它重新开始。已经排队的推送与晨报不受影响——那些在上面的渠道里关。",
  "me.hc.work_lang": "工作语言",
  "me.hc.location_notes": "地点与办公方式",
  "me.hc.currency": "货币",
  "me.hc.salary_floor": "薪资下限",

  // ---- 总览（块 4f）：六个阶段的首页 —— 蓝卡、这周、待你看一眼、它做了什么、对话；阶段 0 的镜子
  // the actions a push carries (stages.json actions): the confirm kinds 2c/4d did not name yet
  "action.confirm.time.yes": "可以",
  "action.confirm.time.no": "不行",
  "action.confirm.criterion.yes": "放到一边",
  "action.confirm.criterion.no": "别放",
  "action.confirm.place_modes.yes": "是，那里也一样",
  "action.confirm.place_modes.no": "不，那里都行",
  // the blue card · the first week
  "home.scan.k": "今天 · 扫描",
  "home.scan.line": "今天推给你的。低匹配按规则不进推送。",
  "home.scan.none": "还没有搜索结果。第一批会在首次搜索后出现。",
  "home.scan.untouched": "本周新抓到、还没动",
  "home.scan.untouchedN": "高 %1 · 中 %2",
  "home.scan.open": "打开岗位 →",
  // the blue card · searching
  "home.motion.k": "进行中",
  "home.motion.closing": "即将截止",
  "home.motion.closingNote": "收藏和待投里七天内要截止的条数。",
  "home.motion.open": "打开岗位 →",
  // the blue card · interviewing
  "home.iv.k": "下一场面试",
  "home.iv.today": "今天",
  "home.iv.tomorrow": "明天",
  "home.iv.inDays": "%1 天后",
  "home.iv.round": "第 %1 轮 · %2",
  "home.iv.prep": "打开面试准备 →",
  "home.iv.noRound": "收到了邀请，时间还没写下。写进面试页，日历和准备才跟得上。",
  "home.iv.write": "写下时间 →",
  // the blue card · offers
  "home.offers.k": "进行中",
  "home.offers.n": "%1 份 offer",
  "home.offers.open": "打开对比 →",
  // the blue card · afterward
  "home.done.k": "结束后",
  "home.done.ended": "已在 %1 记成「搜索结束」。",
  "home.done.scope": "你的搜索范围还在",
  "home.done.scopeSub": "规则、收藏、这些账都留着。想再看，一步恢复。",
  "home.done.restart": "重新开始找 →",
  "home.done.data": "你的数据",
  "home.done.dataSub": "导出你提供的内容（简历、资料、设置、对话等，附机器可读的 data.json）。",
  "home.done.settings": "去设置 →",
  "home.done.say": "推送与夜里的扫描",
  "home.done.saySub": "已经排队的推送还会发完，之后夜里的扫描不再为你跑。想再开始，设置页里按「重新开始找」。",
  // the closing ledger
  "home.ledger.k": "%1 周",
  "home.ledger.since": "从 %1 起",
  "home.ledger.week": "最后一周：读了 %1 条，值得 %2 条。",
  "home.ledger.saved": "收藏",
  "home.ledger.applied": "投过",
  "home.ledger.interview": "面过",
  "home.ledger.offer": "offer",
  "home.ledger.ruler": "累计：「投过」含后来进了面试、拿到 offer、被拒的。岗位页的漏斗数的是此刻的状态，两个数字不该对上。",
  // the week
  "home.week.k": "我的面试日历",
  "home.week.all": "完整日历 →",
  "home.week.none": "这周没有排面试。",
  // 待你看一眼
  "home.story.title": "关于你经历的 %1 个问题",
  "home.story.lede": "可选。回答会写进你的画像概述，之后的推荐会用上。",
  "home.story.open": "回答",
  "home.story.dialog": "简历还不能完全说明的几件事",
  "home.story.left": "还有 %1 个",
  "home.story.answer": "你的回答",
  "home.story.answerPh": "一两句具体的事实就好。",
  "home.story.save": "保存",
  "home.story.skip": "跳过这题",
  "home.story.skipAll": "全部跳过",
  "home.story.note": "关掉之后，会按你的回答重写一次画像概述，下一次搜索起生效。",
  "home.story.failed": "没能保存（%1），再试一次。",
  "home.story.gone": "这题已经不在了：在别处答过，或者换了简历。",
  "home.story.closeFailed": "回答都已保存，但没能排上重写概述（%1），再关一次试试。",
  "home.look.k": "待你看一眼",
  "home.look.lead": "这几件还在桌上，等你处理。",
  "home.look.empty": "今天没有轮到你的。这本身也是一个结果。",
  "home.look.open": "打开 →",
  "home.look.today_ready": "今天的推荐到了",
  "home.look.quiet_day": "今天没有新的高匹配",
  "home.look.question": "有一个问题等你答",
  "home.look.reply_seen": "%1 有回音",
  "home.look.prep_ready": "%1 的面试准备好了",
  "home.look.report_ready": "定位报告写好了",
  "home.look.time_confirm": "%1 发来时间，确认一下",
  "home.look.offer_decision": "%1 的 offer 等你决定",
  "home.look.roundWhen": "%1 · %2 · 第 %3 轮 · %4",
  "home.look.reschedule": "换时间去面试页 →",
  "home.look.pushed": "推送 · %1",
  // 它做了什么
  "home.did.k": "它做了什么",
  "home.did.teaser": "今天新到 %1 条，%2 高 / %3 中 / %4 低。",
  "home.did.teaserNone": "还没有为你跑过岗位搜索。",
  "home.did.run": "最近一次运行",
  "home.did.runLine": "今天新到 %1 条启事，%2 条过了你的硬约束，判了 %3 条：%4 高 / %5 中 / %6 低。",
  "home.did.budgetLeft": "本轮未判的留到下一次。",
  "home.did.runCite": "出处：%1 %2–%3 的运行。",
  "home.did.learned": "学到",
  "home.did.rule": "%1 · %2",
  "home.did.ruleCite": "出处：%1",
  // A rule as it stands once settled — one wording wherever a settled rule
  // is shown: the home's 学到, the divider under the conversation that
  // proposed it (Issue 556).
  "rule.settled.confirmed": "已答：记下了 · %1",
  "rule.settled.declined": "已答：不要 · %1",
  "rule.settled.retired": "已撤回 · %1",
  "home.did.learnedNone": "这周没有新学到的规则。",
  // the conversation
  "home.talk.k": "对话",
  "home.talk.title": "随时问",
  "home.first.k": "第一次搜索",
  "home.first.elsewhere": "其他地方",
  "home.first.noMode": "未说明",
  "home.first.nowhere": "不考虑",
  "home.first.progressTitle": "正在真实搜索市场",
  "home.first.doneTitle": "第一次搜索已经跑完",
  "home.first.stoppedTitle": "第一次搜索停住了",
  "home.first.safe": "可以安全离开这页，岗位在后台继续搜索。",
  "home.first.state.queued": "已排队，等待搜索任务开始。",
  "home.first.state.unknown": "任务已被取走，正在等待可核实的运行记录。",
  "home.first.state.failed": "这次搜索没有完成。已保留可核实的结果和失败状态。",
  "home.first.state.stuck": "搜索长时间没有新进度，需要检查。已保留可核实的状态。",
  "home.first.state.partial": "这一轮先读到这里，剩下的岗位留到下一轮继续读。",
  "home.first.state.partialExtras": "岗位都读完了。有些后台补充这次没做完，不影响下面的结果。",
  "home.first.state.done": "第一次搜索跑完了。",
  "home.first.phase.finding": "正在市场上找岗位。",
  "home.first.phase.reading": "正在逐条读岗位，对照你的条件。",
  "home.first.phase.extras": "已经有 %1 条可以看了，剩下的还在整理。",
  "home.first.phase.more": "还在为你的其他方向找岗位。",
  "home.first.phase.moreReady": "已经有 %1 条可以看了，还在为你的其他方向找岗位。",
  "home.first.phase.extrasEmpty": "岗位都读完了，这一轮没有可展示的高/中匹配。",
  "home.first.phase.extrasEmptyLeft": "已读的来源里还没有可展示的高/中匹配，剩下的来源今晚读完。",
  "home.first.phase.sorting": "岗位都读完了，正在把能看的整理出来。",
  "home.first.phase.stopping": "这次搜索中途出了问题，正在收尾。",
  "home.first.state.cardsShort": "岗位都读完了，但有些没能整理成卡片；这一轮先看到已经整理好的。",
  "home.first.retry": "重试首次搜索",
  "home.first.retrying": "正在排队重试…",
  "home.first.retryError": "未能排入重试；上一次搜索状态没有改变。",
  "home.first.notifyTitle": "可选通知",
  "home.first.notifyLede": "连接更多平台，别错过任何消息。",
  "home.first.notify.email": "Email",
  "home.first.notify.slack": "Slack",
  "home.first.notify.whatsapp": "WhatsApp",
  "home.first.notify.telegram": "Telegram",
  "home.first.resultsK": "第一批结果",
  "home.first.resultsTitle": "第一批值得细看的岗位",
  "home.first.resultsSub": "显示 %1 个，共 %2 个真实匹配。",
  "home.first.resultsSoFar": "已经有 %1 个可以看了，其余的还在读，会陆续加进来。",
  "home.first.empty": "第一次搜索已经结束，但目前没有可展示的高/中匹配。不会为了填满列表而降低你的硬条件。",
  "home.first.emptyWaiting": "第一批结果会出现在这里。搜索还在进行，准备好的岗位会陆续加入。",
  "home.first.emptyStopped": "这一轮还没有可展示的岗位；首搜进度卡会说明搜索或整理停在了哪里。",
  "home.first.sourcesLeft": "还有 %1 个来源这次没来得及读，今晚会读完，新岗位明早出现在今日推荐。",
  "home.first.emptySourcesLeft": "已经读过的来源里还没有可展示的高/中匹配；剩下的来源今晚读完，新岗位明早出现在今日推荐。不会为了填满列表而降低你的硬条件。",
  "home.first.open": "打开",
  "home.first.all": "查看全部岗位 →",
  "home.first.criteria": "你的搜索条件",
  "home.first.criteriaOpen": "检查或修改条件 →",
  "home.first.report": "市场定位报告",
  "home.first.reportOpen": "查看报告 →",
  "home.first.reportWriting": "报告正在基于同一份简历、画像和目标市场生成。",
  "home.first.reportFailed": "报告这次没有写成；打开后可查看原因或重试。",
  "home.first.ask": "问这批岗位",
  "home.first.ask1": "为什么第一条排在最前面？",
  "home.first.ask2": "哪一条最符合我想延续的工作方式？",
  "home.first.askEmpty1": "为什么没有岗位通过我的硬条件？",
  "home.first.askEmpty2": "哪个条件对市场范围影响最大？",
  "home.first.opener.lead": "这次搜索读了 %1 个岗位，%2 个值得细看。",
  "home.first.opener.topOne": "排在最前的一个：",
  "home.first.opener.topTwo": "排在最前的两个：",
  "home.first.opener.ask": "想知道为什么这样排、哪个最适合你，直接问我；也可以在岗位列表里打开看详情。",
  "home.first.opener.askOne": "想知道它为什么排在最前、适不适合你，直接问我；也可以在岗位列表里打开看详情。",
  "home.first.opener.empty": "第一批岗位工具目前没有读到可展示的高/中匹配。",
  "home.first.opener.kept": "你的硬条件没有被自动放宽。",
  "home.first.opener.running": "第一次搜索还没跑完，读到的岗位会直接出现在这一页。",
  "home.first.opener.stopped": "第一次搜索这次没跑完。",
  "home.first.opener.change": "想调整搜索条件，直接跟我说，下一轮就按新的来。",
  "home.opener.reportTyping": "定位报告生成中",
  "home.opener.reportOpen": "查看定位报告 →",
  "home.opener.reportStalled": "你的市场定位报告这次没写成。",
  "home.opener.reportWhy": "去看原因，或者重写一份 →",
  "home.opener.scan": "今天扫完：%1 高 / %2 中 / %3 低。本周还有 %4 条高匹配、%5 条中匹配你没动。规则想改、岗位想跳过，直接说。",
  "home.opener.scanNone": "还没有为你跑过岗位搜索。规则想先改，直接说。",
  "home.opener.motion": "今天的高匹配就在这一页上。想先看哪个、或者条件要改，直接说。",
  "home.opener.iv": "%1 是硬点。有什么要过一遍的，直接说。",
  "home.opener.ivNone": "面试的时间还没写下。",
  "home.opener.offers": "offer 的事可以在这里一起过：怎么比、怎么谈、接不接。合同条款我不解读法律含义。",
  "home.opener.done": "搜索已经结束。想重新开始，去设置里按一下。",
  "home.stats.k": "数据统计",
  "home.stats.close": "收起 ▴",
  "home.stats.open": "展开 ▾",
  "home.today.region": "今日推荐",
  "home.today.k": "今天 · %1",
  "home.today.title": "今日高匹配 %1 个",
  "home.today.left": "还剩 %1 个没看，另外 %2 个你已经答过了。中、低匹配在「查看今日所有推荐」里。",
  "home.today.leftOnly": "还剩 %1 个没看。中、低匹配在「查看今日所有推荐」里。",
  "home.today.none": "今天还没有新的高匹配。中、低匹配在「查看今日所有推荐」里。",
  "home.today.unreadMay": "这次搜索还有 %1 个可能合适的岗位没细看。",
  "home.today.unreadUnlikely": "这次搜索有 %1 个岗位标题看着不对口，没有细看。",
  "home.today.unreadBoth": "这次搜索还有 %1 个可能合适的岗位没细看；另有 %2 个标题看着不对口，也没有细看。",
  "home.today.allAnswered": "今天的高匹配你都答过了。",
  "home.today.batch": "第 %1 批，共 %2 批 · 每批 %3 个",
  "home.today.prev": "← 上一批",
  "home.today.next": "下一批 →",
  "home.today.more": "还有 %1 个在「岗位」页。",
  "home.today.answered": "答过的 %1 个",
  "home.today.unfold": "展开 ▾",
  "home.asks.motion.first": "今天这些里哪个最值得先看？",
  "home.asks.motion.closest": "哪个最贴近我想继续做的事？",
  "home.asks.motion.week": "这周和上周比怎么样？",
  "home.asks.iv.likely": "%1 最可能问什么？",
  "home.asks.iv.mock": "陪我做一次模拟面试",
  "home.asks.iv.week": "这周的面试排得开吗？",
  "home.asks.offers.pay": "谈薪时我该注意什么？",
  "home.asks.offers.before": "接之前还要问清楚什么？",
  // stage 0 · the conversation and the mirror
  "home.ob.title": "先聊几句",
  "home.ob.opener.resume": "先把简历给我。上传 PDF 或 Word 文件，或把经历贴进来。我能从简历推出的会直接写进右边，推不出的再问你几句。",
  "home.ob.opener.ask": "推不出的只问这 %1 题。第一题：%2",
  "home.ob.ask.location_notes": "你想在哪里工作？对去办公室的频率有什么要求？",
  "home.ob.ask.work_lang": "你能用哪些语言工作？",
  "home.ob.ask.direction": "你要找的是哪一类岗位？（后端、平台、机器学习、数据……）",
  "home.ob.ask.salary_floor": "年薪底线是多少（税前，%1）？",
  "home.ob.mirror": "我的资料",
  "home.ob.full": "打开完整页 →",
  "home.ob.hint": "这里只显示对话里已经写下的。要改，直接在对话里说；某一块要细改，也可以打开我的资料。",
  "home.ob.report": "市场定位报告",
  "home.ob.need": "要生成它，我需要：",
  "home.ob.resume": "简历",
  "home.ob.reportAfter": "这几项齐了，我会在对话里写报告。",
  "home.ob.reportFailed": "上一次报告没写成。去「我的资料」看原因。",
  "home.ob.reportWriting": "报告正在写。写完在「我的资料」里打开。",
  "home.ob.reportReady": "输入齐了。在对话里说一声「写报告」。",
  "home.ob.reportDone": "最新一份 · %1 · 打开 →",
  "home.ob.resumeNone": "还没有。交一份之后，经历和技能会出现在这里。",
  "home.ob.resumeMeta": "%1 · %2 段经历 · %3 个学位 · %4 项技能",
  "home.ob.upload": "上传简历",
  "home.ob.uploading": "上传中…",
  "home.ob.tooBig": "超过 10MB",
  "home.ob.profile": "画像",
  "home.ob.profileNone": "「你是谁」从简历推。交了简历再看。",
  "home.ob.hc": "硬约束与目标方向",
  "home.ob.notAsked": "还没问",
  "home.ob.said": "你说的",
  "home.ob.resumeSrc": "从简历推",
  "home.ob.inferred": "推断",
  "home.ob.q": "推不出的这几题",
  // the admin pages (block 5b)
  "admin.title": "后台",
  "admin.nav.label": "后台导航",
  "admin.nav.runs": "运行",
  "admin.nav.log": "访问",
  "admin.nav.models": "模型",
  "admin.nav.sources": "来源",
  "admin.nav.render": "翻译缓存",
  "admin.nav.eval": "匹配评测",
  "admin.nav.spend": "模型花费",
  "admin.nav.payments": "付款异常",
  "admin.spend.head": "模型花费",
  "admin.spend.lede": "每次付费的模型调用都在付费当时记一行账（model_spend）。这里按人、按这个人自己时区的日期汇总；点一个格子看那天的每一笔。只有 id、邮箱、步骤、模型、金额和 token，不显示任何正文。明细在库里保留 90 天，之后按天归档成文件存进归档桶。",
  "admin.spend.cap": "每人每日上限（budgets.user.day）：%1",
  "admin.spend.capNone": "没有设每人每日上限（budgets.user.day）",
  "admin.spend.portalNot": "暂不含网页端（聊天等）的花费",
  "admin.spend.none": "最近 8 天账本里没有记录。",
  "admin.spend.col.who": "谁",
  "admin.spend.market": "全站任务（market，按 UTC 日）",
  "admin.spend.ledger": "账本合计",
  "admin.spend.runs": "当天运行记的花费",
  "admin.spend.reckonNote": "运行按开始那天算；跨过午夜的运行会在两天上都显示差额，不一定是漏记。",
  "admin.spend.day": "%1 在 %2：%3，共 %4 笔",
  "admin.spend.times": "时间按 %1 显示；「哪一天」按这个人自己的时区切",
  "admin.spend.dayNone": "这一天没有记录。",
  "admin.spend.col.at": "时间",
  "admin.spend.col.run": "运行 · 步骤",
  "admin.spend.col.task": "任务",
  "admin.spend.col.model": "模型 · 平台",
  "admin.spend.col.usd": "金额",
  "admin.spend.col.tokens": "输入 / 输出 token",
  "admin.payments.head": "付款异常",
  "admin.payments.lede": "已经付款、但开通函数（fulfil）拒绝了的 Stripe 付款：价格对不上套餐、账号已不存在等。每笔一行，未处理的排前面。账号的到期日没有被改动。",
  "admin.payments.none": "没有被拒的付款。",
  "admin.payments.open": "未处理：%1 笔",
  "admin.payments.col.at": "时间",
  "admin.payments.col.who": "账号",
  "admin.payments.col.plan": "套餐",
  "admin.payments.col.amount": "金额",
  "admin.payments.col.why": "原因",
  "admin.payments.col.session": "Checkout Session",
  "admin.payments.col.state": "状态",
  "admin.payments.gone": "账号已不存在",
  "admin.payments.deleting": "删除中",
  "admin.payments.unresolved": "未处理",
  "admin.payments.honoured": "这笔同时已开通，无需处理",
  "admin.payments.resolved": "%1 已处理：%2",
  "admin.payments.capped": "只显示最近 %1 笔。",
  "admin.payments.how": "处理：补时长用 python -m pipeline.cli grant，退款在 Stripe 后台（点 session 打开该笔付款）；处理完用 python -m pipeline.cli payment-resolve <session> --note \"怎么处理的\" 标记，用户页面上的提示随之消失。",
  "admin.nav.back": "← 回到门户",
  "admin.whoDev": "开发机：前面没有那道门，谁打开都算管理员。",
  "admin.runs.head": "运行",
  "admin.runs.lede": "抓取、判读、翻译、研究，以及页面上点出来的任务——每一段跑了什么、跑到哪、花了多少。排着队没起来的、停了心跳的，排在最前面。",
  "admin.runs.none": "还没有记录。下一轮定时任务会写第一条。",
  "admin.runs.waiting": "%1 条排着队还没被取走",
  "admin.runs.waitingNote": "门户写下的，等流水线的 drain 来取。都等了很久，说明没有 drain 在跑。",
  "admin.runs.stalled": "%1 段停了心跳",
  "admin.runs.unlikely": "标题打成 unlikely 的岗位里有值得推荐的：近 %3 天抽查 %2 个，%1 个值得推荐",
  "admin.runs.unlikelyNote": "95% 区间 %1–%2，下界高于 %3。定时搜索不读这一档（只抽查），考虑把 rates.judge.read_unlikely 设为 1。ops 段 %4 的检查。",
  "admin.runs.stalledNote": "它们的状态还写着「进行中」，但心跳超过 %1 分钟没动。ops 段满三小时才会把状态改成「卡住」——在那之前，这一行是唯一的说法。",
  "admin.runs.clock": "定时任务",
  "admin.runs.clockWoke": "调度上次醒来：%1",
  "admin.runs.clockNever": "还没有 drain 醒来过：没有东西在排定时任务。",
  "admin.runs.clockAsleep": "调度已经 %1 分钟没有醒来：定时任务没人排。",
  "admin.runs.clockAsleepNote": "常驻 drain 负责排所有定时任务（没有 crontab）。超过 %1 分钟没醒，就是它停了或卡住了：看它的 systemd 单元，或在机器上跑 cli schedule。",
  "admin.runs.clockZones": "%1 个时区",
  "admin.runs.clockNext": "下次 %1",
  "admin.runs.clockLast": "上次 %1 · %2",
  "admin.runs.clockNotYet": "还没到过点",
  "admin.runs.notes": "%1 %2 的晨报",
  "admin.runs.nights": "%1 %2 的夜间工作",
  "admin.runs.runs": "最近的运行",
  "admin.runs.capped": "只列最近 %1 段。",
  "admin.log.capped": "只列最近来过的 %1 个地址，共 %2 个。",
  "admin.runs.silent": "%1 分钟没动静",
  "admin.runs.took": "用时 %1",
  "admin.runs.steps": "步骤",
  "admin.runs.col.state": "状态",
  "admin.runs.col.did": "进度",
  "admin.runs.col.cost": "花费",
  "admin.runs.col.took": "用时 · 备注",
  "admin.runs.market": "市场",
  "admin.runs.open": "展开",
  "admin.runs.close": "收起",
  "admin.runs.gate": "闸拒了 %1 条模型输出",
  "admin.state.running": "进行中",
  "admin.state.ready": "完成",
  "admin.state.partial": "部分完成",
  "admin.state.failed": "失败",
  "admin.state.stuck": "卡住",
  "admin.step.running": "进行中",
  "admin.step.ok": "完成",
  "admin.step.partial": "部分完成",
  "admin.step.skipped": "跳过",
  "admin.step.failed": "失败",
  "admin.log.head": "访问记录",
  "admin.log.lede": "谁来过这个站，从哪来，来过几次。一次访问 = 中间没有超过 %1 分钟空当的一串请求。",
  "admin.log.none": "还没有记录。下一次有人打开页面就会写下第一条。",
  "admin.log.ip": "IP 地址",
  "admin.log.place": "所在地",
  "admin.log.device": "设备",
  "admin.log.first": "首次",
  "admin.log.last": "最后",
  "admin.log.n": "访问次数",
  "admin.log.total": "%1 个地址，%2 次访问，%3 个请求",
  "admin.log.local": "本机",
  "admin.log.unknown": "未知",
  "admin.log.zone": "时间按 %1",
  "admin.log.theirTime": "按 %1 时间看",
  "admin.log.myTime": "按我的时间看",
  "admin.log.visits": "%1 次访问",
  "admin.log.requests": "%1 个请求",
  "admin.log.entry": "入口",
  "admin.log.from": "来源",
  "admin.log.lang": "语言",
  "admin.log.signedIn": "登录为 %1",
  "admin.log.gone": "这个地址没有记录。",
  "admin.log.visit": "这次访问",
  "admin.log.backToIp": "← %1",
  "admin.log.backToAll": "← 所有地址",
  "admin.dev.mobile": "手机",
  "admin.dev.tablet": "平板",
  "admin.dev.desktop": "桌面",
  "admin.dev.bot": "机器人",
  "admin.dev.unknown": "不明",
  "admin.models.head": "模型",
  "admin.models.lede": "哪个模型做哪件事，现在就生效的那份。改 models.json 或本机的覆盖文件，下一次调用就换——不用重启。这一页只读。",
  "admin.models.task": "任务",
  "admin.models.model": "模型",
  "admin.models.source": "来自",
  "admin.models.price": "价格 / 百万 token",
  "admin.models.ceiling": "单次上限",
  "admin.models.backend": "谁在跑",
  "admin.models.why": "为什么是它",
  "admin.models.src.env": "环境变量",
  "admin.models.src.local": "本机文件",
  "admin.models.src.repo": "仓库",
  "admin.models.files": "仓库 %1",
  "admin.models.localHere": "本机覆盖 %1（在）",
  "admin.models.localGone": "本机覆盖 %1（没有）",
  "admin.models.prices": "价格核对于 %1",
  "admin.models.broken": "models.json 读不了：%1",
  "admin.models.noPrice": "没有价格",
  "admin.src.head": "来源",
  "admin.src.lede": "岗位是从这些板子上来的。一行 = 一个系统上的一个公司板子；「选中」是上一次抓取时有几个用户的源列表要它（每次抓取前都会按当前的板把列表重算一遍），零就是那次抓取没读它。",
  "admin.src.none": "没有匹配的板子。",
  "admin.src.search": "搜板子、公司",
  "admin.src.board": "板子",
  "admin.src.company": "公司",
  "admin.src.origin": "来路",
  "admin.src.read": "最近读",
  "admin.src.count": "上次条数",
  "admin.src.open": "在册",
  "admin.src.chosen": "选中",
  "admin.src.never": "从没读过",
  "admin.src.totals": "%1 个板子 · %2 个在册岗位 · %3 个用户选过来源",
  "admin.src.unwanted": "%1 个没有任何用户要",
  "admin.src.unread": "%1 个从没读过",
  "admin.src.dormant": "休眠 · 自 %1",
  "admin.src.failing": "连续失败 %1 次 · 自 %2",
  "admin.src.partial": "只读到一部分 · 自 %1",
  "admin.src.partialN": "%1 个只读到一部分（读不全时不关岗位，这些岗位改由各自的页面判断是否已下架）",
  "admin.src.dormantN": "%1 个休眠（一直读不到：不再挡住同公司岗位的关闭，每晚仍会读，读到就恢复）",
  "admin.src.noChoosers": "还没有用户跑过 select_sources，所以「选中」全是零，夜里读的是全部。",
  "admin.src.agg": "聚合源",
  "admin.src.aggNone": "还没有找到任何聚合源站点。夜里的 sweep 会为每个有人在找的国家去找。",
  "admin.src.sort.read": "最近读",
  "admin.src.sort.open": "在册岗位",
  "admin.src.sort.board": "名字",
  "admin.src.sort.chosen": "选中数",
  "admin.src.sortBy": "排序",
  "admin.src.origin.registry": "注册表",
  "admin.src.origin.lead": "线索",
  "admin.src.origin.discover": "反挖",
  "admin.src.origin.paste": "贴的",
  "admin.src.origin.seed": "播种",
  "admin.src.clear": "清空筛选",
  "admin.render.head": "翻译缓存",
  "admin.render.lede": "网页上模型写的文字按读者的界面语言从这张缓存里取，没有的由门户排队、夜里的 render 段去翻。这里只有数字、契约、语言、写入方和失败原因，不显示任何原文或译文。",
  "admin.render.window": "近 7 天 = %1 起，按 %2 时间",
  "admin.render.stale": "%1 条认领超过 %2 分钟还没完成",
  "admin.render.staleNote": "状态还是 running：认领它的运行崩掉或超时了。门户下次上屏会重新排队（已试满次数的不会），尝试次数已经算过；一直出现说明 render 段在反复失败。",
  "admin.render.cache": "缓存",
  "admin.render.cacheNone": "缓存里还没有行。",
  "admin.render.engines": "写入方：legacy 是 render-backfill 抄进来的旧译文，same 是原文本来就是目标语言（不花钱），其余是写它的模型。字符数按原文算。",
  "admin.render.col.contract": "契约",
  "admin.render.col.lang": "译成",
  "admin.render.col.engine": "写入方",
  "admin.render.col.week": "近 7 天",
  "admin.render.col.all": "全部",
  "admin.render.col.rows": "行",
  "admin.render.col.chars": "字符",
  "admin.render.col.cost": "花费",
  "admin.render.col.day": "日期",
  "admin.render.col.state": "状态",
  "admin.render.col.now": "现在",
  "admin.render.col.asked": "近 7 天提出",
  "admin.render.col.reason": "原因",
  "admin.render.col.n": "条数",
  "admin.render.col.last": "最近一次",
  "admin.render.total": "合计",
  "admin.render.days": "每天写入",
  "admin.render.requests": "等待中的请求",
  "admin.render.requestsNone": "没有排队、进行中或失败的请求。",
  "admin.render.state.queued": "排队",
  "admin.render.state.running": "进行中",
  "admin.render.state.failed": "失败",
  "admin.render.state.refused": "不再翻译",
  "admin.render.oldest": "排得最久的一条 %1 起，已等 %2 分钟",
  "admin.render.givenUp": "%1 条已试满 %2 次，不再自动重试",
  "admin.render.failures": "失败原因（前 10）",
  "admin.render.failuresNone": "没有失败的请求。",
  "admin.render.masked": "原因只按类型显示，引到的数字、链接和文字一概不出现；认不出的算「other」。",
  "admin.render.gate": "闸拒（近 7 天）",
  "admin.render.gateNone": "近 7 天闸没有拒过 render 的输出。",
  // the matching report (#929)
  "admin.eval.head": "匹配评测",
  "admin.eval.lede": "judge 的展示、打分、分档和排序，和用户实际的决定对得上多少。用户每处理够一定数量的岗位就自动重算一次；全体是各人最新一份相加。",
  "admin.eval.threshold": "阈值：每处理 %1 个岗位算一次",
  "admin.eval.swept": "上次检查 %1",
  "admin.eval.neverSwept": "还没有检查过",
  "admin.eval.caveats": "口径与偏差",
  "admin.eval.caveat.exposure": "曝光偏差：用户只能对展示给他们的岗位做决定。比率只在「judge 展示过、用户处理过」的岗位里算；没处理的不进分母，也不算负向。",
  "admin.eval.caveat.newest": "以当前的决定（最新一条）为准；撤销回到没有决定的，算没处理。",
  "admin.eval.caveat.rejudged": "judgment 只留最新一版：做决定之后才被重判的，按新判断算（下面会列出条数）。",
  "admin.eval.caveat.score": "分数用用户现在的权重、岗位在做决定那天的年龄；分档线用的是页面打开时生效的那组。",
  "admin.eval.caveat.rank": "排序：判这条岗位那一晚 judge 的阅读顺序（rank_grade，0109 起才记）；更早判的算「无记录」。",
  "admin.eval.overall": "全体",
  "admin.eval.none": "还没有报告。有人处理满 %1 个岗位后，下一次检查会写第一份。",
  "admin.eval.personHead": "%1 的报告",
  "admin.eval.personNone": "这个用户还没有报告。",
  "admin.eval.personClose": "收起",
  "admin.eval.people": "各用户进度",
  "admin.eval.peopleNone": "还没有人做过决定。",
  "admin.eval.col.user": "用户",
  "admin.eval.col.fresh": "距上次新处理 / 阈值",
  "admin.eval.col.lastReport": "上次报告",
  "admin.eval.col.lastDecision": "最近一次决定",
  "admin.eval.never": "还没有",
  "admin.eval.history": "历史",
  "admin.eval.historyNone": "还没有算过。",
  "admin.eval.col.at": "时间",
  "admin.eval.col.whose": "对象",
  "admin.eval.col.trigger": "触发",
  "admin.eval.col.upto": "读到的决定 id",
  "admin.eval.everyone": "全体",
  "admin.eval.trigger.auto": "自动（到阈值）",
  "admin.eval.trigger.manual": "手动（命令行）",
  "admin.eval.col.positive": "想要",
  "admin.eval.col.maybe": "收藏",
  "admin.eval.col.passed": "不适合",
  "admin.eval.oldShape": "%1 的这份报告格式已过期，下一次计算会换成新的。",
  "admin.eval.counted": "%1 算的，共处理 %2 个岗位，%3 人",
  "admin.eval.notes": "展示过的里面 %1 条在决定后被重判，%2 条用户手动改过档",
  "admin.eval.col.what": "岗位",
  "admin.eval.shown": "展示过（进分母）",
  "admin.eval.hidden": "judge 没展示（想要的 = judge 漏掉）",
  "admin.eval.unjudged": "没有判断（用户自己带来的，或岗位已下架）",
  "admin.eval.byBand": "按分档（分档线 high %1 / mid %2）",
  "admin.eval.col.band": "档",
  "admin.eval.col.rate": "想要的比例（95% 区间）",
  "admin.eval.col.kept": "想要或收藏（95% 区间）",
  "admin.eval.curve": "按分数（每 0.05 一格）",
  "admin.eval.col.score": "分数",
  "admin.eval.rank": "按当晚的标题分档与阅读位次",
  "admin.eval.col.grade": "标题分档",
  "admin.eval.grade.likely": "likely",
  "admin.eval.grade.maybe": "maybe",
  "admin.eval.grade.unlikely": "unlikely",
  "admin.eval.grade.none": "没给档（按 maybe 读）",
  "admin.eval.grade.absent": "无记录",
  "admin.eval.col.place": "位次",
  "admin.eval.firstK": "前 %1 名里的占比",
  "admin.eval.reasons": "不适合的理由（展示过的）",
  "admin.eval.reasonsNone": "还没有人给过理由。",
  "admin.eval.col.reason": "理由",
  "admin.eval.col.total": "次数",
  "admin.eval.custom": "用户自己的理由",
  // ---- 登录 / 注册 / 试用（块 6）：门是这个应用自己的了，不再是外面那道 Google 门
  "auth.signIn": "登录",
  "auth.signUp": "注册",
  "auth.email": "邮箱",
  "auth.password": "密码",
  "auth.forgot": "忘记密码",
  "auth.other": "使用其他账号登录",
  "auth.google": "用 Google 继续",
  "auth.noAccount": "还没有账号？",
  "auth.toSignUp": "注册 →",
  "auth.haveAccount": "已经有账号？",
  "auth.toSignIn": "登录 →",
  "auth.signOut": "登出",
  "auth.signedOut": "已经登出了。",
  "auth.working": "请稍候…",
  "auth.signOutFailed": "没能登出，请再试一次。",
  // 查不了会话时 proxy 回的 503 页（#260）
  "unavailable.title": "服务暂时不可用",
  "unavailable.body": "稍后刷新页面就好，不需要重新登录。",
  "unavailable.retry": "刷新",
  // 六位验证码那一屏
  "auth.code.head": "查收邮箱",
  "auth.code.sent": "6 位验证码已发到 %1，%2 分钟内有效。",
  "auth.code.label": "验证码",
  "auth.code.submit": "验证并进入",
  "auth.code.resend": "重新发送",
  "auth.code.resendIn": "%1 秒后可以重发",
  "auth.code.another": "换个邮箱",
  "auth.code.devNote": "这台机器没有配发信账号，验证码打在服务端控制台里。",
  // 出错时说的话。%1 是分钟数或剩余次数
  "auth.err.email": "填一个能收信的邮箱。",
  "auth.err.password": "密码至少 10 个字符。",
  "auth.err.credentials": "邮箱或密码不对。",
  "auth.err.locked": "失败次数太多，%1 分钟后再试。",
  "auth.err.code": "验证码不对，还可以试 %1 次。",
  "auth.err.codeGone": "这个验证码过期或者用过了，重新发一封。",
  "auth.err.tooMany": "发得太频繁了，过一会儿再试。",
  "auth.err.tooManyWait": "发得太频繁了，%1 秒后再试。",
  "auth.err.mail": "信没发出去，稍后再试一次。",
  "auth.err.failed": "没能完成，稍后再试一次。",
  "auth.err.google": "用 Google 登录没走通，再试一次，或者用邮箱和密码登录。",
  "auth.err.session": "登录状态过期了，重新登录一次。",
  // 发给邮箱的两封信（不是界面文案，但语言取当时那一页的语言）。%1 验证码，%2 分钟
  "auth.mail.code.subject": "你的 Counting AI 验证码",
  "auth.mail.code.body": "验证码：%1\n\n回到注册页面填进去就行，%2 分钟内有效。\n不是你本人的话，忽略这封信，什么都不会发生。",
  "account.email.oneOnly": "登录邮箱也是唯一通知邮箱。更换后原邮箱不再用于登录或通知；必须先验证新邮箱。",
  "account.email.new": "新邮箱",
  "account.email.send": "验证新邮箱",
  "account.email.code": "验证码",
  "account.email.verify": "确认更换",
  "account.email.cancel": "取消",
  "account.email.sent": "验证码已发送到新邮箱，验证成功前当前邮箱保持不变。",
  "account.email.taken": "这个邮箱已属于另一个账号。请用它登录，或找回那个账号。",
  "account.email.same": "这已经是当前登录邮箱。",
  "account.email.wrong": "验证码不对，请重试。",
  "account.email.rate": "请稍等后再发送新的验证码。",
  "account.email.failed": "没有更换成功。验证码可能已过期，请重新发送。",
  "account.email.mailSubject": "验证你的新 Counting AI 邮箱",
  "account.email.mailBody": "验证码：%1\n\n回到 Counting AI 设置页填入，%2 分钟内有效。验证成功前旧邮箱保持不变。不是你本人操作的话，忽略即可。",
  "auth.mail.exists.subject": "这个邮箱已经有 Counting AI 账号了",
  "auth.mail.exists.body": "刚才有人用这个邮箱注册 Counting AI，而它已经有账号了，所以我们没有新建。\n\n如果是你：直接去登录页登录；密码忘了就用「忘记密码」。\n如果不是你：忽略这封信，你的账号没有任何变化。",
  "auth.mail.none.subject": "这个邮箱还没有 Counting AI 账号",
  "auth.mail.none.body": "刚才有人用这个邮箱申请重设 Counting AI 的密码，而它还没有账号，所以我们什么都没改。\n\n如果是你：去注册页建一个就行。\n如果不是你：忽略这封信。",
  "auth.err.taken": "这个邮箱已经注册过了，直接登录吧。",
  // 重设密码
  "auth.reset.head": "重设密码",
  "auth.reset.lede": "填你的邮箱，我们发一个验证码过去。",
  "auth.reset.new": "新密码",
  "auth.reset.submit": "设为新密码",
  "auth.reset.sent": "如果这个邮箱有账号，验证码已经发过去了。",
  "auth.reset.done": "密码换好了，其它设备上的登录都退掉了。",
  // 试用与订阅（§21）
  "billing.head": "订阅",
  "billing.active": "订阅进行中。",
  "billing.trialLeft": "试用还剩 %1 天",
  "billing.trialLeftOne": "试用还剩 1 天",
  "billing.trialEndsOn": "试用到 %1",
  "billing.expired": "试用已经结束了。",
  "billing.lede": "你的数据都还在，可以导出带走。想接着用，或者要删除账号和数据，写信给我们。",
  "billing.lede.paid": "订阅在跑，什么都不用做。想带走数据，下面的按钮随时可用；要删除账号和数据，写信给我们。",
  "billing.contact": "写信开通 →",
  "billing.write": "写信给我们 →",
  "billing.export": "导出我的数据",
  "billing.signedInAs": "当前登录：%1",
  "billing.back": "回到门户",
  "billing.plan.month.name": "1 个月",
  "billing.plan.quarter.name": "3 个月",
  "billing.plan.year.name": "12 个月",
  "billing.plan.month.item": "Prospect · 1 个月",
  "billing.plan.quarter.item": "Prospect · 3 个月",
  "billing.plan.year.item": "Prospect · 12 个月",
  "billing.perMonth": "折合每月 %1",
  "billing.off": "省 %1%",
  "billing.buy": "购买",
  "billing.renew": "续费",
  "billing.subscribe": "订阅",
  "billing.opening": "正在打开付款页…",
  "billing.methods": "支持 %1",
  "billing.method.apple_pay": "Apple Pay",
  "billing.method.google_pay": "Google Pay",
  "billing.method.alipay": "支付宝",
  "billing.method.wechat_pay": "微信支付",
  "billing.once": "一次性付款，不会自动续费。",
  "billing.lede.buy": "选一个时长，付款后马上开通。你的数据都还在。",
  "billing.lede.early": "现在买不会浪费剩下的试用：从试用结束那天往后算。",
  "billing.lede.renew": "续费从当前到期日往后顺延。",
  "billing.lede.delete": "要删除账号和数据，写信给我们。",
  "billing.paidUntil": "已付费至 %1",
  "billing.success.head": "付款成功",
  "billing.success.until": "%1已开通，可以用到 %2。",
  "billing.success.see": "查看订阅",
  "billing.pending": "这笔付款还没确认。如果已经付过，稍等片刻再刷新这一页。",
  "billing.refused": "我们收到了你的一笔付款，但没能自动开通。我们会尽快人工处理（补上时长或退款）。",
  "billing.refusedWrite": "我们收到了你的一笔付款，但没能自动开通。我们会尽快人工处理（补上时长或退款）；有疑问请写信到 %1。",
  "billing.err.off": "现在还不能在线付款，请写信给我们开通。",
  "billing.err.stripe": "没能打开付款页，请稍后再试。",
  "billing.err.failed": "没能打开付款页，请检查网络后再试。",
  // A button pressed on a push, on any transport (press.*, #626); Slack's own lines and the modal behind the ones that need words (slack.*, #256).
  "press.chose": "已选：%1",
  "press.failed": "这条没能记下来，去网页上答吧。",
  "press.answered": "这个问题已经回答过了。",
  "press.decision.new": "未选择",
  "press.decision.same": "这个岗位已经是「%1」了。",
  "press.decision.change": "这个岗位现在是「%1」，要改成「%2」吗？",
  "press.decision.changed": "已从「%1」改成「%2」。",
  "slack.decision.moved": "弹窗打开后，这个岗位已变成「%1」。如仍要修改，请重新点击消息里的按钮。",
  "press.trialOver": "试用已经结束，去网页看看。",
  "press.moved": "这个岗位刚在别处变成了「%1」。要改的话，再按一次。",
  "press.reasons": "为什么不适合？可以多选，也可以不选；想多说一句就按「写原因」。选好按「提交」。",
  "press.submitFor": "提交 · %1",
  "press.note": "写原因",
  "press.noteAsk": "回复这条消息，写下「%1」为什么不适合。",
  "press.notePlaceholder": "为什么不适合",
  "press.noteSaved": "记下了。选好理由后，按「%1」。",
  "press.noteLate.submitted": "这个岗位已经提交了，这句原因没有记上。",
  "press.noteLate.cancelled": "这个岗位的理由已经取消了，这句原因没有记上。",
  "press.noteLost": "这句原因没有记上：这个岗位已经在别处定了。",
  "press.cancel": "取消",
  // A Telegram morning's three answers, a third of a phone's row each (#765): shorter than the card's own (card.goApply …).
  "press.pick.preparing": "申请",
  "press.pick.maybe": "收藏",
  "press.pick.passed": "不适合",
  "press.changeTo": "改成「%1」",
  "press.onSite": "这个要在网页上答。",
  "press.open": "打开",
  "slack.modal.change": "修改选择",
  "slack.modal.changeSubmit": "确认修改",
  "slack.modal.answer": "回答",
  "slack.modal.when": "换个时间",
  "slack.modal.what": "换个说法",
  "slack.modal.answerLabel": "你的回答",
  "slack.modal.currencyLabel": "货币",
  "slack.modal.whenLabel": "什么时候可以",
  "slack.modal.whatLabel": "应该是怎样的",
  "slack.modal.submit": "发送",
  "slack.modal.close": "取消",
  "slack.modal.empty": "还没写呢。",
  "press.salaryUnread": "没读懂：写一个年薪下限，比如 70k 或 70000 GBP。",
  "slack.modal.why": "不适合我",
  "slack.modal.reasonsLabel": "哪里不合适（可不选）",
  "slack.modal.whyLabel": "一句话理由，我用来学（可不填）",
  "slack.noModal": "现在打不开输入框，请到网页上回答。",
  "slack.noReason": "已记为「不适合我」，理由没能问你。",
  "slack.notRewritten": "已记下，但没能更新上面那条消息，它的按钮可能还在。",
  "slack.openSite": "打开网页",
  // Connect Slack, and what a slack row is sent (#256, PR2).
  "me.set.ch.slackLede": "推送会发到你 Slack 的私信里，能直接按按钮回答；连接是去 Slack 授权一次。",
  "me.set.ch.connectSlack": "连接 Slack",
  "me.set.ch.slackUnavailable": "这个部署还没配 Slack app，暂时连不了。",
  "me.set.ch.slack.connected": "连上了。Slack 里应该已经收到一条私信。",
  "me.set.ch.slack.err.oauth": "Slack 那边没有给出 token，没连上。再试一次。",
  "me.set.ch.slack.err.state": "这次授权不是从这个浏览器发起的，没连上。再点一次「连接 Slack」。",
  "me.set.ch.slack.err.client": "这个部署还没配 Slack app。",
  "me.set.ch.slack.err.exists": "已经连着一个 Slack 了；要换一个，先删掉现在这个。",
  "me.set.sub.title": "推送哪些",
  "me.set.sub.today": "今天的新岗位",
  "me.set.sub.interviews": "面试准备好了",
  "me.set.sub.asks": "要我答一句的",
  "me.set.sub.reports": "报告、线索",
  "me.set.sub.account": "账号与试用",
  "slack.connected": "连上了。之后新岗位、要你答的问题、报告和线索、面试准备会发到这里，能直接按按钮回答。",
  "privacy.choice.off": "这个站点目前没有开启浏览器里的记录，所以不会向你征求同意，也不会记录。",
  "me.set.analytics.browserOff": "浏览器里的记录：这个站点目前没有开启。",
  // #788 PR2: the question, the privacy page, the settings card, the error page.
  "consent.title": "帮我们把这里做得更好用？",
  "consent.body": "你同意的话，浏览器会记下你在这里打开了哪些页面、停留多久、用了哪些按钮、页面上哪里出了错。不会记下你写的任何内容、你看的是哪家公司或哪个岗位，也不带你的名字或邮箱。不同意也照常使用。",
  "consent.change": "以后随时可以在「我的资料 → 设置 → 使用统计」里改。",
  "consent.more": "详细说明",
  "consent.yes": "同意",
  "consent.no": "不同意",
  "privacy.title": "隐私与使用统计",
  "privacy.lede": "这一页说明我们为改进产品记录了什么、不记录什么，以及你怎么选择。",
  "privacy.server.h": "服务器上的记录",
  "privacy.server.p": "你在这里做的操作——比如提交 onboarding 的一步、保存设置、发出一条消息——服务器会记下是哪一类操作、成功了没有、用了多长时间；后台任务（读简历、第一次搜索、生成报告）记下用了多久、成没成功。这些是你本来就发给我们的请求，不需要另外征求同意；你可以在「我的资料 → 设置」里关掉。",
  "privacy.browser.h": "浏览器里的记录",
  "privacy.browser.p": "只有你同意之后才有：打开了哪些页面（只记页面类型，比如「岗位详情页」，不记具体是哪一个）、在页面前停留了多久、用了哪些按钮、页面上哪里出了错。",
  "privacy.never.h": "使用统计里从不记录",
  "privacy.never.p": "你写的任何文字、简历内容、你看的是哪家公司或哪个岗位、页面的具体地址、你的 IP 地址和浏览器信息。记录里不出现你的名字、邮箱或账号 id，只有一串由账号算出来的代号。",
  "privacy.where.h": "存在哪里、留多久",
  "privacy.where.p": "记录按批写进我们自己的文件存储，不进数据库，也不交给广告商。以后接入分析平台后，导入完就从我们的存储里删除；在我们的存储里最多保留 730 天。",
  "privacy.source.h": "你从哪里来",
  "privacy.source.p": "如果你第一次是通过带推广标记的链接、或从别的网站来到这里，我们会把那个网站的名字、链接上的标记和你落地的页面类型存在一个 cookie 里 30 天；你注册时它们随账号存下，cookie 随即删除。不记录把你带来的那个页面的具体地址。",
  "privacy.choice.h": "你的选择",
  "privacy.choice.granted": "这个浏览器现在：已同意浏览器里的记录。",
  "privacy.choice.denied": "这个浏览器现在：不同意浏览器里的记录。",
  "privacy.choice.none": "这个浏览器还没有选择。",
  "privacy.choice.server": "服务器上的记录在「我的资料 → 设置」里关。",
  "me.set.analytics.title": "使用统计",
  "me.set.analytics.lede": "用来看大家怎么用这里、哪里慢、哪里出错，从而改进产品。不记录你写的内容、你看的公司和岗位，也不带你的名字。",
  "me.set.analytics.server": "服务器上的记录",
  "me.set.analytics.what.server": "你做了哪一类操作、成功没有、用了多久。关掉后，你的这类记录不再写入。",
  "me.set.analytics.browser": "浏览器里的记录（这个浏览器）",
  "me.set.analytics.what.browser": "打开了哪些页面、停留多久、用了哪些按钮、哪里出错。只在这个浏览器上生效。",
  "me.set.analytics.more": "详细说明",
  "error.title": "这一页出了点问题",
  "error.body": "页面没能显示出来。可以重新加载试试。",
  "error.retry": "重新加载",
} as const;
